import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAction, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";
const ScenarioDisplay = React.lazy(() => import("../components/ScenarioDisplay"));
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Textarea,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Stack,
  Grid,
  GridItem,
  Select,
  NumberInput,
  NumberInputField,
  Divider,
  useToast,
  Badge,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Radio,
  RadioGroup,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";

export default function ScenarioSharePage() {
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');
  const [passwordInput, setPasswordInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [accessCodeInput, setAccessCodeInput] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [startTime] = useState(new Date());
  const [submittedStudents, setSubmittedStudents] = useState(new Set());
  const [userEmail, setUserEmail] = useState("");
  const [manualStudentName, setManualStudentName] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [teamLeaderId, setTeamLeaderId] = useState("");
  const [sendToStudent, setSendToStudent] = useState(false);
  const [studentEmailForEval, setStudentEmailForEval] = useState("");
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' | 'tablet' | 'simple-print'
  const [liveAudioSourceLabel, setLiveAudioSourceLabel] = useState("Participant");
  const [liveAudioRole, setLiveAudioRole] = useState("member");
  const [liveAudioExpiresMinutes, setLiveAudioExpiresMinutes] = useState(180);
  const [liveAudioLinks, setLiveAudioLinks] = useState([]);
  const [liveAudioStatus, setLiveAudioStatus] = useState(null);
  const [liveAudioBusy, setLiveAudioBusy] = useState(false);
  const [evalFormUpdating, setEvalFormUpdating] = useState(false);
  const toast = useToast();
  const resolvedTokenParam = useMemo(() => {
    if (tokenParam) return tokenParam;
    if (typeof window === "undefined") return "";

    const path = window.location.pathname || "";
    const marker = "/scenario-share/";
    const idx = path.indexOf(marker);
    if (idx === -1) return "";
    const tail = path.slice(idx + marker.length);
    const candidate = tail.split("/")[0] || "";
    return decodeURIComponent(candidate);
  }, [tokenParam]);

  // Live scoring state
  const [liveScoreChecks, setLiveScoreChecks] = useState(null);
  
  // NREMT granular evaluation state
  const [nremtEval, setNremtEval] = useState({
    sceneSizeUp: {
      bsiPrecautions: 'notPerformed',
      sceneAssessed: 'notPerformed',
      moiIdentified: 'notPerformed',
      patientCount: 'notPerformed',
      cSpineConsidered: 'notPerformed',
      performance: null,
      notes: ''
    },
    primaryAssessment: {
      generalImpression: 'notPerformed',
      levelOfConsciousness: 'notPerformed',
      chiefComplaint: 'notPerformed',
      airwayAssessed: 'notPerformed',
      breathingRate: 'notPerformed',
      breathingQuality: 'notPerformed',
      pulseAssessed: 'notPerformed',
      skinAssessed: 'notPerformed',
      bleedingChecked: 'notPerformed',
      performance: null,
      notes: ''
    },
    sample: {
      signsSymptoms: 'notPerformed',
      allergies: 'notPerformed',
      medications: 'notPerformed',
      pastHistory: 'notPerformed',
      lastOral: 'notPerformed',
      events: 'notPerformed',
      performance: null,
      notes: ''
    },
    opqrst: {
      onset: 'notPerformed',
      provocation: 'notPerformed',
      quality: 'notPerformed',
      radiation: 'notPerformed',
      severity: 'notPerformed',
      time: 'notPerformed',
      performance: null,
      notes: ''
    },
    vitalSigns: {
      bloodPressure: 'notPerformed',
      pulse: 'notPerformed',
      respiratoryRate: 'notPerformed',
      spo2: 'notPerformed',
      temperature: 'notPerformed',
      bloodGlucose: 'notPerformed',
      performance: null,
      notes: ''
    },
    physicalExam: {
      head: 'notPerformed',
      neck: 'notPerformed',
      chest: 'notPerformed',
      abdomen: 'notPerformed',
      pelvis: 'notPerformed',
      extremities: 'notPerformed',
      back: 'notPerformed',
      performance: null,
      notes: ''
    },
    interventions: {
      oxygenTherapy: 'notPerformed',
      ivAccess: 'notPerformed',
      medications: 'notPerformed',
      immobilization: 'notPerformed',
      bleedingControl: 'notPerformed',
      woundCare: 'notPerformed',
      performance: null,
      notes: ''
    },
    reassessment: {
      repeatedVitals: 'notPerformed',
      reassessedInterventions: 'notPerformed',
      identifiedChanges: 'notPerformed',
      performance: null,
      notes: ''
    },
    overallPerformance: {
      score: null,
      criticalCriteria: [],
      performance: null,
      instructorNotes: ''
    }
  });
  const [criticalFailures, setCriticalFailures] = useState({
    failureToInitiateTransportWithin15Minutes: false,
    failureToTakeOrVerbalizePpe: false,
    failureToDetermineSceneSafety: false,
    failureToProvideAppropriateOxygen: false,
    failureToAssessOrProvideAdequateVentilation: false,
    failureToManageAbcProblems: false,
    failureToDifferentiateImmediateTransportNeed: false,
    detailedHistoryBeforeAbc: false,
    failureToDeterminePrimaryProblem: false,
    dangerousOrInappropriateIntervention: false,
    failureToProvideSpinalProtectionWhenIndicated: false,
  });
  const [scenarioTrackingChecks, setScenarioTrackingChecks] = useState({
    primaryAssessment: {},
    secondaryAssessment: {},
    patientHistory: {},
    interventions: {},
    criticalActions: {},
    pitfalls: {},
  });
  const [traumaEval, setTraumaEval] = useState({
    sceneSizeUp: {},
    primarySurvey: {},
    historyTaking: {},
    secondaryAssessment: {},
    reassessment: {},
    actualStartTime: "",
    actualEndTime: "",
  });
  const [integratedEval, setIntegratedEval] = useState({
    leadershipSceneManagement: 0,
    patientAssessment: 0,
    patientManagement: 0,
    interpersonalRelations: 0,
    integration: 0,
    actualStartTime: "",
    actualTransportTime: "",
    actualEndTime: "",
    mandatoryActionsFailure: false,
    failureCompetentParamedic: false,
    unacceptableAffect: false,
    dangerousIntervention: false,
  });

  // Field labels for evaluation sections
  const fieldLabels = {
    sceneSizeUp: {
      bsiPrecautions: 'Takes/verbalizes BSI precautions',
      sceneAssessed: 'Determines scene safety',
      moiIdentified: 'Identifies mechanism of injury/nature of illness',
      patientCount: 'Determines number of patients',
      cSpineConsidered: 'Considers spinal stabilization'
    },
    primaryAssessment: {
      generalImpression: 'Forms general impression',
      levelOfConsciousness: 'Assesses level of consciousness',
      chiefComplaint: 'Identifies chief complaint',
      airwayAssessed: 'Assesses airway',
      breathingRate: 'Assesses breathing rate',
      breathingQuality: 'Assesses breathing quality',
      pulseAssessed: 'Assesses pulse',
      skinAssessed: 'Assesses skin condition',
      bleedingChecked: 'Checks for major bleeding'
    },
    sample: {
      signsSymptoms: 'Signs and symptoms',
      allergies: 'Allergies',
      medications: 'Medications',
      pastHistory: 'Past medical history',
      lastOral: 'Last oral intake',
      events: 'Events leading to incident'
    },
    opqrst: {
      onset: 'Onset',
      provocation: 'Provocation/Palliation',
      quality: 'Quality',
      radiation: 'Radiation',
      severity: 'Severity',
      time: 'Time/Duration'
    },
    vitalSigns: {
      bloodPressure: 'Blood pressure',
      pulse: 'Pulse rate',
      respiratoryRate: 'Respiratory rate',
      spo2: 'SpO2',
      temperature: 'Temperature',
      bloodGlucose: 'Blood glucose'
    },
    physicalExam: {
      head: 'Head examination',
      neck: 'Neck examination',
      chest: 'Chest examination',
      abdomen: 'Abdominal examination',
      pelvis: 'Pelvic examination',
      extremities: 'Extremity examination',
      back: 'Back examination'
    },
    interventions: {
      oxygenTherapy: 'Oxygen therapy',
      ivAccess: 'IV access',
      medications: 'Medications administered',
      immobilization: 'Immobilization',
      bleedingControl: 'Bleeding control',
      woundCare: 'Wound care'
    },
    reassessment: {
      repeatedVitals: 'Repeated vital signs',
      reassessedInterventions: 'Reassessed interventions',
      identifiedChanges: 'Identified changes in condition'
    }
  };

  // Helper function to check if a section needs attention
  const sectionNeedsAttention = (sectionKey, evalState, liveScores) => {
    const fieldKeys = Object.keys(fieldLabels[sectionKey] || {});
    const hasPerformedItems = fieldKeys.some(key => evalState[key] === 'performed');
    const hasLiveScores = liveScores && Object.values(liveScores[sectionKey] || {}).some(v => v === true);
    const missingPerformance = !evalState.performance;
    
    return (hasPerformedItems || hasLiveScores) && missingPerformance;
  };

  // Screenshot attempt logging
  const [{ }, logScreenshot] = useGlobalAction(api.logScreenshotAttempt);
  
  // Access tracking
  const [{ }, trackAccess] = useGlobalAction(api.trackScenarioTokenAccess);

  // Password validation
  const [{ }, validatePassword] = useGlobalAction(api.validateScenarioSharePassword);
  const [{ data: templatesData, fetching: templatesFetching }, getTemplates] = useGlobalAction(
    api.getAvailableEvaluationTemplates
  );
  const [shareToken, setShareToken] = useState(null);
  const [tokenFetching, setTokenFetching] = useState(true);
  const [tokenLoadError, setTokenLoadError] = useState(null);

  const availableEvaluationForms = useMemo(() => {
    return (templatesData?.templates || []).map((template) => ({
      value: template.key,
      label: template.displayName,
      description: template.description,
    }));
  }, [templatesData]);

  useEffect(() => {
    if (isAuthenticated && shareToken?.createdBy?.id) {
      getTemplates({ userId: shareToken.createdBy.id });
    }
  }, [isAuthenticated, shareToken?.createdBy?.id, getTemplates]);

  const formatFieldLabel = useCallback((key) => {
    return String(key || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());
  }, []);

  const flattenScenarioFields = useCallback((source, idPrefix, labelPrefix = "", options = {}) => {
    const { includeValues = true } = options;
    if (!source || typeof source !== "object" || Array.isArray(source)) return [];

    const rows = [];
    const visit = (obj, keyPath = [], depth = 0) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
      Object.entries(obj).forEach(([key, rawValue]) => {
        const nextPath = [...keyPath, key];
        const id = `${idPrefix}:${nextPath.join(".")}`;
        const value = rawValue;

        if (value && typeof value === "object" && !Array.isArray(value) && depth < 2) {
          visit(value, nextPath, depth + 1);
          return;
        }

        if (Array.isArray(value)) {
          if (value.length === 0) return;
          rows.push({
            id,
            keyPath: nextPath.join("."),
            label: includeValues
              ? `${labelPrefix}${formatFieldLabel(nextPath.join(" / "))}: ${value.join(", ")}`
              : `${labelPrefix}${formatFieldLabel(nextPath.join(" / "))}`,
          });
          return;
        }

        if (value === null || value === undefined || String(value).trim() === "") return;
        rows.push({
          id,
          keyPath: nextPath.join("."),
          label: includeValues
            ? `${labelPrefix}${formatFieldLabel(nextPath.join(" / "))}: ${String(value)}`
            : `${labelPrefix}${formatFieldLabel(nextPath.join(" / "))}`,
        });
      });
    };

    visit(source);
    return rows;
  }, [formatFieldLabel]);

  const scenarioTrackingItems = useMemo(() => {
    const output = shareToken?.scenario?.scenarioOutput || {};
    const interventions = [];
    const interventionGroups = output?.interventions || {};
    const stopWords = new Set(["the", "and", "for", "with", "from", "that", "this", "into", "after", "before", "when", "then", "per", "via", "once", "as", "to", "of", "or", "if"]);
    const compactInterventionLabel = (label) => {
      const words = String(label || "")
        .replace(/[^a-zA-Z0-9\s/-]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
      const filtered = words.filter((w) => !stopWords.has(w.toLowerCase()));
      const chosen = (filtered.length > 0 ? filtered : words).slice(0, 4);
      return chosen.join(" ");
    };

    const parseTextList = (source, idPrefix) => {
      if (!source) return [];
      const values = Array.isArray(source) ? source : [source];
      return values
        .map((item, index) => {
          if (typeof item === "string") {
            const label = item.trim();
            if (!label) return null;
            return { id: `${idPrefix}:${index}`, label };
          }
          if (item && typeof item === "object") {
            const label = String(
              item.label ||
              item.name ||
              item.action ||
              item.title ||
              item.text ||
              item.value ||
              item.item ||
              item.criterion ||
              item.description ||
              ""
            ).trim();
            if (!label) return null;
            return { id: `${idPrefix}:${index}`, label };
          }
          return null;
        })
        .filter(Boolean);
    };

    const collectNestedValuesByKeys = (source, keyNames, maxDepth = 4) => {
      const hits = [];
      const targetKeys = new Set((keyNames || []).map((k) => String(k).toLowerCase()));
      const visit = (value, depth = 0) => {
        if (depth > maxDepth || value == null) return;
        if (Array.isArray(value)) {
          value.forEach((entry) => visit(entry, depth + 1));
          return;
        }
        if (typeof value !== "object") return;
        Object.entries(value).forEach(([key, nested]) => {
          if (targetKeys.has(String(key).toLowerCase())) {
            hits.push(nested);
          }
          visit(nested, depth + 1);
        });
      };
      visit(source, 0);
      return hits;
    };

    const pushInterventionsFromList = (source, groupName) => {
      parseTextList(source, `${groupName}:list`).forEach((item, index) => {
        interventions.push({
          id: `${groupName}:${index}:${item.id}`,
          label: item.label,
          shortLabel: compactInterventionLabel(item.label),
          group: groupName,
        });
      });
    };

    ["LifeSaving", "Secondary", "Optional"].forEach((groupName) => {
      const items = Array.isArray(interventionGroups[groupName]) ? interventionGroups[groupName] : [];
      items.forEach((label, index) => {
        interventions.push({
          id: `${groupName}:${index}`,
          label,
          shortLabel: compactInterventionLabel(label),
          group: groupName,
        });
      });
    });
    pushInterventionsFromList(output?.criticalInterventions, "LifeSaving");
    pushInterventionsFromList(output?.secondaryInterventions, "Secondary");
    pushInterventionsFromList(output?.optionalInterventions, "Optional");
    if (Array.isArray(output?.interventions)) {
      pushInterventionsFromList(output.interventions, "Optional");
    }
    collectNestedValuesByKeys(output, ["interventions", "criticalInterventions", "secondaryInterventions", "optionalInterventions"])
      .forEach((source, idx) => {
        if (source && typeof source === "object" && !Array.isArray(source)) {
          ["LifeSaving", "Secondary", "Optional"].forEach((groupName) => {
            const nestedItems = Array.isArray(source[groupName]) ? source[groupName] : [];
            nestedItems.forEach((label, index) => {
              interventions.push({
                id: `${groupName}:nested:${idx}:${index}`,
                label,
                shortLabel: compactInterventionLabel(label),
                group: groupName,
              });
            });
          });
          return;
        }
        pushInterventionsFromList(source, "Optional");
      });

    const criticalActions = [
      ...parseTextList(output?.criticalActions, "ca"),
      ...parseTextList(output?.CriticalActions, "ca:legacy"),
      ...parseTextList(output?.mandatoryActions, "ca:mandatory"),
      ...parseTextList(output?.EducationalComponents?.CriticalActions, "ca:edu"),
      ...collectNestedValuesByKeys(output, ["criticalActions", "mandatoryActions"]).flatMap((v, i) =>
        parseTextList(v, `ca:nested:${i}`)
      ),
    ];
    const pitfalls = [
      ...parseTextList(output?.commonPitfalls, "pit"),
      ...parseTextList(output?.CommonPitfalls, "pit:legacy"),
      ...parseTextList(output?.pitfalls, "pit:extra"),
      ...parseTextList(output?.EducationalComponents?.CommonPitfalls, "pit:edu"),
      ...collectNestedValuesByKeys(output, ["commonPitfalls", "pitfalls"]).flatMap((v, i) =>
        parseTextList(v, `pit:nested:${i}`)
      ),
    ];
    const primaryAssessment = [
      ...flattenScenarioFields(output?.primaryAssessment, "pa", "", { includeValues: false }),
      ...flattenScenarioFields(output?.primarySurvey, "pa:survey", "", { includeValues: false }),
    ];
    const secondaryAssessment = [
      ...flattenScenarioFields(output?.secondaryAssessment, "sa", "", { includeValues: false }),
      ...flattenScenarioFields(output?.focusedAssessment, "sa:focused", "", { includeValues: false }),
      ...flattenScenarioFields(output?.detailedAssessment, "sa:detailed", "", { includeValues: false }),
    ];
    const patientHistory = [
      ...flattenScenarioFields(output?.history, "hx", "", { includeValues: false }),
      ...flattenScenarioFields(output?.hpi, "hx:hpi", "", { includeValues: false }),
      ...flattenScenarioFields(output?.historyOfPresentIllness, "hx:hpi2", "", { includeValues: false }),
      ...flattenScenarioFields(output?.sampleHistory, "hx:sample", "", { includeValues: false }),
      ...flattenScenarioFields(output?.opqrstHistory, "hx:opqrst", "", { includeValues: false }),
      ...collectNestedValuesByKeys(output, ["history", "hpi", "historyOfPresentIllness"]).flatMap((v, i) =>
        flattenScenarioFields(v, `hx:nested:${i}`, "", { includeValues: false })
      ),
    ];

    const dedupeByLabel = (items) => {
      const seen = new Set();
      return items.filter((item) => {
        const key = String(item?.label || "").toLowerCase().trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    return {
      interventions: dedupeByLabel(interventions),
      criticalActions: dedupeByLabel(criticalActions),
      pitfalls: dedupeByLabel(pitfalls),
      primaryAssessment: dedupeByLabel(primaryAssessment),
      secondaryAssessment: dedupeByLabel(secondaryAssessment),
      patientHistory: dedupeByLabel(patientHistory),
    };
  }, [shareToken?.scenario?.scenarioOutput, flattenScenarioFields]);

  useEffect(() => {
    setScenarioTrackingChecks((prev) => {
      const next = {
        interventions: {},
        criticalActions: {},
        pitfalls: {},
        primaryAssessment: {},
        secondaryAssessment: {},
        patientHistory: {},
      };
      scenarioTrackingItems.interventions.forEach((item) => {
        next.interventions[item.id] = prev.interventions[item.id] || false;
      });
      scenarioTrackingItems.criticalActions.forEach((item) => {
        next.criticalActions[item.id] = prev.criticalActions[item.id] || false;
      });
      scenarioTrackingItems.pitfalls.forEach((item) => {
        next.pitfalls[item.id] = prev.pitfalls[item.id] || false;
      });
      scenarioTrackingItems.primaryAssessment.forEach((item) => {
        next.primaryAssessment[item.id] = prev.primaryAssessment[item.id] || false;
      });
      scenarioTrackingItems.secondaryAssessment.forEach((item) => {
        next.secondaryAssessment[item.id] = prev.secondaryAssessment[item.id] || false;
      });
      scenarioTrackingItems.patientHistory.forEach((item) => {
        next.patientHistory[item.id] = prev.patientHistory[item.id] || false;
      });
      return next;
    });
  }, [scenarioTrackingItems]);

  const isAutoCheckedIntervention = useCallback((fullLabel) => {
    const label = String(fullLabel || "").toLowerCase();
    const live = liveScoreChecks?.interventions || {};
    const evalInterventions = nremtEval?.interventions || {};

    const matched = (field) => evalInterventions[field] === "performed" || live[field] === true;

    if (label.includes("oxygen")) return matched("oxygenTherapy");
    if (label.includes("iv")) return matched("ivAccess");
    if (label.includes("medication") || label.includes("epinephrine") || label.includes("drug")) return matched("medications");
    if (label.includes("bleed")) return matched("bleedingControl");
    if (label.includes("wound")) return matched("woundCare");
    if (label.includes("immobil") || label.includes("spinal")) return matched("immobilization");
    return false;
  }, [liveScoreChecks, nremtEval?.interventions]);

  const integratedSupportCounters = useMemo(() => {
    const checkedIntervention = (item) =>
      !!scenarioTrackingChecks.interventions[item.id] || isAutoCheckedIntervention(item.label);
    const criticalInterventions = scenarioTrackingItems.interventions.filter((item) => item.group === "LifeSaving");
    const secondaryInterventions = scenarioTrackingItems.interventions.filter((item) => item.group === "Secondary");
    const optionalInterventions = scenarioTrackingItems.interventions.filter((item) => item.group === "Optional");
    const primaryPerformed = scenarioTrackingItems.primaryAssessment.filter(
      (item) => !!scenarioTrackingChecks.primaryAssessment[item.id]
    ).length;
    const secondaryAssessmentPerformed = scenarioTrackingItems.secondaryAssessment.filter(
      (item) => !!scenarioTrackingChecks.secondaryAssessment[item.id]
    ).length;
    const patientHistoryPerformed = scenarioTrackingItems.patientHistory.filter(
      (item) => !!scenarioTrackingChecks.patientHistory[item.id]
    ).length;

    return {
      criticalPerformed: criticalInterventions.filter(checkedIntervention).length,
      criticalTotal: criticalInterventions.length,
      secondaryPerformed: secondaryInterventions.filter(checkedIntervention).length,
      secondaryTotal: secondaryInterventions.length,
      optionalPerformed: optionalInterventions.filter(checkedIntervention).length,
      optionalTotal: optionalInterventions.length,
      pitfallsPerformed: scenarioTrackingItems.pitfalls.filter(
        (item) => !!scenarioTrackingChecks.pitfalls[item.id]
      ).length,
      patientHistoryPerformed,
      patientHistoryTotal: scenarioTrackingItems.patientHistory.length,
      primarySurveyPerformed: primaryPerformed,
      primarySurveyTotal: scenarioTrackingItems.primaryAssessment.length,
      primarySurveyComplete:
        scenarioTrackingItems.primaryAssessment.length > 0 &&
        primaryPerformed === scenarioTrackingItems.primaryAssessment.length,
      secondaryAssessmentPerformed,
      secondaryAssessmentTotal: scenarioTrackingItems.secondaryAssessment.length,
    };
  }, [scenarioTrackingChecks, scenarioTrackingItems, isAutoCheckedIntervention]);

  const toggleScenarioTracking = useCallback((group, id) => {
    setScenarioTrackingChecks((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [id]: !prev[group]?.[id],
      },
    }));
  }, []);

  // View mode handlers
  const handleTabletView = useCallback(() => {
    setViewMode(prev => prev === 'tablet' ? 'desktop' : 'tablet');
  }, []);

  const handleSimplePrint = useCallback(() => {
    setViewMode('simple-print');
    // Add simple-print class to body
    document.body.classList.add('simple-print');
    setTimeout(() => {
      window.print();
      // Remove class after print dialog closes
      setTimeout(() => {
        document.body.classList.remove('simple-print');
        setViewMode('desktop');
      }, 500);
    }, 100);
  }, []);

  const handleDownloadPDF = useCallback(() => {
    toast({
      title: "PDF Download",
      description: "Use your browser's print dialog and select 'Save as PDF'",
      status: "info",
      duration: 5000,
      isClosable: true
    });
    handleSimplePrint();
  }, [handleSimplePrint, toast]);

  const handleAccessibilityView = useCallback(() => {
    if (viewMode === 'accessibility') {
      document.body.classList.remove('accessibility-mode');
      setViewMode('desktop');
    } else {
      document.body.classList.add('accessibility-mode');
      setViewMode('accessibility');
    }
  }, [viewMode]);

  // Handle live score checkbox changes - receives full updated state from ScenarioDisplay
  const handleLiveScoreCheck = useCallback((newCheckState) => {
    console.log('=== LIVE SCORE CHECK ===');
    console.log('New check state:', newCheckState);
    console.log('=======================');
    
    // Update the live score checks state
    setLiveScoreChecks(newCheckState);
    
    // Direct mapping from scenario check keys to evaluation form sections
    const evalMapping = {
      // Scene Size-Up mappings
      'sceneSize': {
        'bsi': ['sceneSizeUp', 'bsiPrecautions'],
        'bsiPrecautions': ['sceneSizeUp', 'bsiPrecautions'],
        'sceneAssessed': ['sceneSizeUp', 'sceneAssessed'],
        'safety': ['sceneSizeUp', 'sceneAssessed'], // Bidirectional: Scene safety maps to scene assessed
        'moiIdentified': ['sceneSizeUp', 'moiIdentified'],
        'patientCount': ['sceneSizeUp', 'patientCount'],
        'cSpineConsidered': ['sceneSizeUp', 'cSpineConsidered']
      },
      // HPI (History of Present Illness) - One-way mapping only
      // When HPI is checked in scenario, it auto-checks "events leading up to" in SAMPLE
      // But checking "events" in SAMPLE does NOT auto-check HPI (prevents circular logic)
      'hpi': {
        'hpi': ['sample', 'events']
      },
      // Primary Assessment mappings
      'primary': {
        'generalImpression': ['primaryAssessment', 'generalImpression'],
        'levelOfConsciousness': ['primaryAssessment', 'levelOfConsciousness'],
        'chiefComplaint': ['primaryAssessment', 'chiefComplaint'],
        'airwayAssessed': ['primaryAssessment', 'airwayAssessed'],
        'breathingRate': ['primaryAssessment', 'breathingRate'],
        'breathingQuality': ['primaryAssessment', 'breathingQuality'],
        'pulseAssessed': ['primaryAssessment', 'pulseAssessed'],
        'skinAssessed': ['primaryAssessment', 'skinAssessed'],
        'bleedingChecked': ['primaryAssessment', 'bleedingChecked']
      },
      'primaryAssessment': {
        'generalImpression': ['primaryAssessment', 'generalImpression'],
        'levelOfConsciousness': ['primaryAssessment', 'levelOfConsciousness'],
        'chiefComplaint': ['primaryAssessment', 'chiefComplaint'],
        'airwayAssessed': ['primaryAssessment', 'airwayAssessed'],
        'breathingRate': ['primaryAssessment', 'breathingRate'],
        'breathingQuality': ['primaryAssessment', 'breathingQuality'],
        'pulseAssessed': ['primaryAssessment', 'pulseAssessed'],
        'skinAssessed': ['primaryAssessment', 'skinAssessed'],
        'bleedingChecked': ['primaryAssessment', 'bleedingChecked']
      },
      // SAMPLE mappings
      'sample': {
        'signsSymptoms': ['sample', 'signsSymptoms'],
        'allergies': ['sample', 'allergies'],
        'medications': ['sample', 'medications'],
        'pastHistory': ['sample', 'pastHistory'],
        'lastOral': ['sample', 'lastOral'],
        'events': ['sample', 'events']
      },
      // OPQRST mappings
      'opqrst': {
        'onset': ['opqrst', 'onset'],
        'provocation': ['opqrst', 'provocation'],
        'quality': ['opqrst', 'quality'],
        'radiation': ['opqrst', 'radiation'],
        'severity': ['opqrst', 'severity'],
        'time': ['opqrst', 'time']
      },
      // Vitals mappings
      'vitals': {
        'bloodPressure': ['vitalSigns', 'bloodPressure'],
        'pulse': ['vitalSigns', 'pulse'],
        'respiratoryRate': ['vitalSigns', 'respiratoryRate'],
        'spo2': ['vitalSigns', 'spo2'],
        'temperature': ['vitalSigns', 'temperature'],
        'bloodGlucose': ['vitalSigns', 'bloodGlucose']
      },
      'vitalSigns': {
        'bloodPressure': ['vitalSigns', 'bloodPressure'],
        'pulse': ['vitalSigns', 'pulse'],
        'respiratoryRate': ['vitalSigns', 'respiratoryRate'],
        'spo2': ['vitalSigns', 'spo2'],
        'temperature': ['vitalSigns', 'temperature'],
        'bloodGlucose': ['vitalSigns', 'bloodGlucose']
      },
      // Physical Exam mappings
      'secondary': {
        'head': ['physicalExam', 'head'],
        'neck': ['physicalExam', 'neck'],
        'chest': ['physicalExam', 'chest'],
        'abdomen': ['physicalExam', 'abdomen'],
        'pelvis': ['physicalExam', 'pelvis'],
        'extremities': ['physicalExam', 'extremities'],
        'back': ['physicalExam', 'back']
      },
      'physicalExam': {
        'head': ['physicalExam', 'head'],
        'neck': ['physicalExam', 'neck'],
        'chest': ['physicalExam', 'chest'],
        'abdomen': ['physicalExam', 'abdomen'],
        'pelvis': ['physicalExam', 'pelvis'],
        'extremities': ['physicalExam', 'extremities'],
        'back': ['physicalExam', 'back']
      },
      // Interventions mappings
      'interventions': {
        'oxygenTherapy': ['interventions', 'oxygenTherapy'],
        'ivAccess': ['interventions', 'ivAccess'],
        'medications': ['interventions', 'medications'],
        'immobilization': ['interventions', 'immobilization'],
        'bleedingControl': ['interventions', 'bleedingControl'],
        'woundCare': ['interventions', 'woundCare']
      },
      // Reassessment mappings
      'reassessment': {
        'repeatedVitals': ['reassessment', 'repeatedVitals'],
        'reassessedInterventions': ['reassessment', 'reassessedInterventions'],
        'identifiedChanges': ['reassessment', 'identifiedChanges']
      }
    };
    
    // Build updated evaluation state
    setNremtEval(prev => {
      const updated = { ...prev };
      
      // Iterate through all categories in the new check state
      Object.entries(newCheckState || {}).forEach(([category, items]) => {
        const categoryMapping = evalMapping[category];
        if (!categoryMapping) {
          console.warn(`No mapping found for category: ${category}`);
          return;
        }
        
        // Iterate through all items in this category
        Object.entries(items || {}).forEach(([itemKey, checked]) => {
          const mapping = categoryMapping[itemKey];
          if (mapping) {
            const [section, field] = mapping;
            console.log(`Mapping ${category}.${itemKey} -> ${section}.${field} = ${checked ? 'performed' : 'notPerformed'}`);
            
            // Update the evaluation state
            if (!updated[section]) {
              updated[section] = { ...prev[section] };
            }
            updated[section] = {
              ...updated[section],
              [field]: checked ? 'performed' : 'notPerformed'
            };
          } else {
            console.warn(`No mapping found for ${category}.${itemKey}`);
          }
        });
      });
      
      return updated;
    });
  }, []);

  // Evaluation checkbox state (legacy - keeping for backward compatibility)
  const [evaluationChecks, setEvaluationChecks] = useState({
    sceneSize: {},
    primary: {},
    sample: {},
    opqrst: {},
    vitals: {},
    secondary: {},
    assessment: {}
  });

  // Handle evaluation checkbox changes from scenario (legacy)
  const handleEvaluationCheckChange = (newData) => {
    setEvaluationChecks(newData);
  };

  // Auto-fill email from URL on mount
  useEffect(() => {
    if (emailFromUrl && !emailInput) {
      setEmailInput(emailFromUrl);
    }
  }, [emailFromUrl]);

  // Always load token via action/internal API to avoid public model read filter issues
  useEffect(() => {
    if (!resolvedTokenParam) return;

    let cancelled = false;
    setTokenFetching(true);
    setTokenLoadError(null);
    console.log("[scenario-share] Loading token context", {
      routeToken: tokenParam || null,
      resolvedToken: resolvedTokenParam,
    });

    (async () => {
      try {
        const response = await fetch(`/scenario-share-token/${encodeURIComponent(resolvedTokenParam)}`, {
          method: "GET",
          headers: { "Accept": "application/json" },
          cache: "no-store",
        });
        const result = await response.json();
        const tokenData = result?.token || null;
        if (!cancelled && tokenData) {
          setShareToken(tokenData);
          console.log("[scenario-share] Token context loaded", {
            tokenId: tokenData.id,
            cohortId:
              tokenData?.simulationSession?.cohort?.id ||
              tokenData?.simulationSession?.cohortId ||
              tokenData?.simulationSession?.scenarioRun?.cohort?.id ||
              tokenData?.simulationSession?.scenarioRun?.cohortId ||
              tokenData?.cohort?.id ||
              tokenData?.cohortId ||
              null,
            hasScenarioRun: !!tokenData?.simulationSession?.scenarioRun,
          });
        } else if (!cancelled) {
          setTokenLoadError(new Error(result?.error || "Invalid scenario token"));
          console.error("[scenario-share] Token context missing in response", result);
        }
      } catch (error) {
        if (!cancelled) {
          setTokenLoadError(error);
          console.error("[scenario-share] Token context fetch failed", error);
        }
      } finally {
        if (!cancelled) {
          setTokenFetching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedTokenParam, tokenParam]);

  // Initialize live score checks when scenario loads
  useEffect(() => {
    if (shareToken?.scenario?.scenarioOutput && !liveScoreChecks) {
      const checkStructure = {};
      setLiveScoreChecks(checkStructure);
    }
  }, [shareToken?.scenario?.scenarioOutput, liveScoreChecks]);

  // Access code validation function
  const handleAccessCodeSubmit = (e) => {
    e.preventDefault();
    
    if (!accessCodeInput.trim()) {
      setPasswordError("Please enter an access code");
      return;
    }
    
    // Simple string comparison with the access code from the token
    if (accessCodeInput.trim() === shareToken?.accessCode) {
      setIsAuthenticated(true);
      setUserEmail(shareToken.createdBy?.email || 'instructor');
      setPasswordError("");
    } else {
      setPasswordError("Invalid access code. Please try again.");
    }
  };

  const scenarioRunStudents = useMemo(() => {
    const rawStudents = shareToken?.simulationSession?.scenarioRun?.students;
    if (!Array.isArray(rawStudents)) return [];

    const parseName = (value) => {
      const cleaned = String(value || "").trim();
      if (!cleaned) return { firstName: "", lastName: "" };
      const parts = cleaned.split(/\s+/);
      return {
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" "),
      };
    };

    const normalized = rawStudents
      .map((entry, index) => {
        if (!entry) return null;
        if (typeof entry === "string") return null;
        const id =
          entry.id ||
          entry.userId ||
          entry.studentId ||
          entry.memberId ||
          null;
        if (!id) return null;

        const fullName = entry.fullName || entry.name || entry.studentName || entry.displayName || "";
        const parsedName = parseName(fullName);
        return {
          id,
          firstName: entry.firstName || parsedName.firstName || "",
          lastName: entry.lastName || parsedName.lastName || "",
          email: entry.email || entry.studentEmail || "",
          primaryRole: entry.primaryRole || entry.role || "student",
          sourceIndex: index,
        };
      })
      .filter(Boolean);

    const seen = new Set();
    return normalized.filter((student) => {
      if (seen.has(student.id)) return false;
      seen.add(student.id);
      return true;
    });
  }, [shareToken?.simulationSession?.scenarioRun?.students]);

  const cohortId =
    shareToken?.simulationSession?.cohort?.id ||
    shareToken?.simulationSession?.cohortId ||
    shareToken?.simulationSession?.scenarioRun?.cohort?.id ||
    shareToken?.simulationSession?.scenarioRun?.cohortId ||
    shareToken?.cohort?.id ||
    shareToken?.cohortId ||
    null;
  const hasSeededRunStudents = scenarioRunStudents.length > 0;
  // Check if we're in No Cohort mode
  const isNoCohortMode = !cohortId && !hasSeededRunStudents;
  const liveAudioRunId = shareToken?.simulationSession?.sessionId || null;

  // Fetch cohort members (includes role information)
  const [{ data: cohortMembers, fetching: fetchingStudents }] = useFindMany(api.cohortMembership, {
    filter: {
      cohort: { id: { equals: cohortId } },
      active: { equals: true }
    },
    select: {
      id: true,
      primaryRole: true,
      user: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      }
    },
    pause: !isAuthenticated || isNoCohortMode || !cohortId
  });

  // Extract and sort students from cohort members
  const cohortStudents = useMemo(() => {
    const fromMemberships = Array.isArray(cohortMembers)
      ? cohortMembers
          .map((m) => ({
            ...m.user,
            primaryRole: m.primaryRole || "student",
          }))
          .filter((student) => !!student?.id)
      : [];

    const base = fromMemberships.length > 0 ? fromMemberships : scenarioRunStudents;
    return base
      .slice()
      .sort((a, b) => {
        const aIsInstructor = a.primaryRole === "instructor";
        const bIsInstructor = b.primaryRole === "instructor";
        if (aIsInstructor !== bIsInstructor) return aIsInstructor ? 1 : -1;

        const lastNameA = (a.lastName || "").toLowerCase();
        const lastNameB = (b.lastName || "").toLowerCase();
        if (lastNameA !== lastNameB) return lastNameA.localeCompare(lastNameB);

        const firstNameA = (a.firstName || "").toLowerCase();
        const firstNameB = (b.firstName || "").toLowerCase();
        if (firstNameA !== firstNameB) return firstNameA.localeCompare(firstNameB);

        return String(a.id).localeCompare(String(b.id));
      });
  }, [cohortMembers, scenarioRunStudents]);

  // Fetch existing evaluations for this scenario
  const [{ data: existingEvaluations, fetching: fetchingEvals, error: evalsError }] = useFindMany(
    api.ph4456Evaluation,
    {
      filter: {
        // Query by session instead of just scenario for real-time coordination
        simulationSession: { id: { equals: shareToken?.simulationSession?.id } }
      },
      select: {
        id: true,
        studentId: true, // Use ID field instead of relationship
        student: { 
          id: true, 
          firstName: true, 
          lastName: true, 
          email: true 
        },
        evaluationDate: true,
        status: true
      },
      // Pause if no session ID
      pause: !isAuthenticated || !shareToken?.simulationSession?.id,
    }
  );

  // Handle permission errors gracefully - evaluations list is optional
  useEffect(() => {
    if (evalsError) {
      console.warn('Could not fetch existing evaluations (this is OK - feature is optional):', evalsError.message);
      // Set empty evaluations so the rest of the form works
      setSubmittedStudents(new Set());
    }
  }, [evalsError]);

  useEffect(() => {
    if (existingEvaluations) {
      const evaluated = new Set(
        existingEvaluations
          .map(e => e.student?.id || e.studentId) // Fallback to studentId if student is null
          .filter(Boolean)
      );
      setSubmittedStudents(evaluated);
    }
  }, [existingEvaluations]);

  // Form state for evaluation
  const [formData, setFormData] = useState({
    sceneSize: false,
    bsiPrecautions: false,
    sceneHazards: false,
    mechanismOfInjury: false,
    numberOfPatients: false,
    needForResources: false,
    generalImpression: false,
    levelOfConsciousness: false,
    chiefComplaint: false,
    airwayPatency: false,
    breathingAdequacy: false,
    circulationAssessment: false,
    priorityDecision: false,
    historyPresent: false,
    opqrst: false,
    sample: false,
    pertinentNegatives: false,
    pertinentPositives: false,
    vitalSigns: false,
    physicalExam: false,
    diagnosticTools: false,
    interventionRepeated: false,
    vitalSignsTrend: false,
    overallScore: 0,
    comments: "",
    completionTime: 0,
    studentId: "",
    studentName: "",
  });

  // Sync evaluation checks with form data
  useEffect(() => {
    // Update form data when checkboxes change
    setFormData(prev => ({
      ...prev,
      // Map checkbox state to form fields
      bsiPrecautions: evaluationChecks.sceneSize?.bsi || prev.bsiPrecautions,
      chiefComplaint: evaluationChecks.primary?.chiefComplaint || prev.chiefComplaint,
      vitalSigns: evaluationChecks.vitals?.recorded || prev.vitalSigns,
      sample: Object.values(evaluationChecks.sample || {}).every(Boolean) || prev.sample,
      opqrst: Object.values(evaluationChecks.opqrst || {}).every(Boolean) || prev.opqrst,
      physicalExam: evaluationChecks.secondary?.physicalExam || prev.physicalExam
    }));
  }, [evaluationChecks]);

  // Calculate total score based on checked items
  useEffect(() => {
    const checkboxFields = [
      'sceneSize', 'bsiPrecautions', 'sceneHazards', 'mechanismOfInjury', 'numberOfPatients', 'needForResources',
      'generalImpression', 'levelOfConsciousness', 'chiefComplaint', 'airwayPatency', 'breathingAdequacy', 
      'circulationAssessment', 'priorityDecision', 'historyPresent', 'opqrst', 'sample', 'pertinentNegatives', 
      'pertinentPositives', 'vitalSigns', 'physicalExam', 'diagnosticTools', 'interventionRepeated', 'vitalSignsTrend'
    ];
    
    const checkedCount = checkboxFields.filter(field => formData[field]).length;
    const totalPossible = checkboxFields.length;
    const calculatedScore = Math.round((checkedCount / totalPossible) * 100);
    
    // Only update if different to avoid infinite loop
    if (formData.overallScore !== calculatedScore) {
      setFormData(prev => ({ ...prev, overallScore: calculatedScore }));
    }
  }, [
    formData.sceneSize, formData.bsiPrecautions, formData.sceneHazards, formData.mechanismOfInjury, 
    formData.numberOfPatients, formData.needForResources, formData.generalImpression, 
    formData.levelOfConsciousness, formData.chiefComplaint, formData.airwayPatency, 
    formData.breathingAdequacy, formData.circulationAssessment, formData.priorityDecision, 
    formData.historyPresent, formData.opqrst, formData.sample, formData.pertinentNegatives, 
    formData.pertinentPositives, formData.vitalSigns, formData.physicalExam, formData.diagnosticTools, 
    formData.interventionRepeated, formData.vitalSignsTrend
  ]);

  const [{ data: submitData, fetching: submitting, error: submitError }, submit] = useAction(
    api.ph4456Evaluation.create
  );

  // Check token validity
  const isTokenValid = () => {
    if (!shareToken) return false;
    if (!shareToken.isActive) return false;
    if (shareToken.expiresAt && new Date(shareToken.expiresAt) < new Date()) return false;
    if (shareToken.maxUses && shareToken.currentUses >= shareToken.maxUses) return false;
    return true;
  };

  const fetchLiveAudioStatus = useCallback(async () => {
    if (!isAuthenticated || !liveAudioRunId) return;

    setLiveAudioBusy(true);
    try {
      const response = await fetch(`/live-audio/status?runId=${encodeURIComponent(liveAudioRunId)}`, {
        method: "GET",
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || "Unable to fetch live audio status");
      }
      setLiveAudioStatus(data);
    } catch (error) {
      toast({
        title: "Live Audio Status Error",
        description: error.message || "Unable to fetch status",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLiveAudioBusy(false);
    }
  }, [isAuthenticated, liveAudioRunId, toast]);

  const handleIssueLiveAudioLink = useCallback(async () => {
    if (!isAuthenticated || !liveAudioRunId) return;

    setLiveAudioBusy(true);
    try {
      const response = await fetch("/live-audio/issue-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          runId: liveAudioRunId,
          sourceLabel: liveAudioSourceLabel || "Participant",
          participantRole: liveAudioRole || "member",
          expiresInMinutes: Number(liveAudioExpiresMinutes) || 180,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || "Unable to issue participant link");
      }

      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const joinUrl = `${origin}/live-audio/${data.token}?runId=${encodeURIComponent(liveAudioRunId)}`;
      setLiveAudioLinks((prev) => [
        {
          token: data.token,
          sourceId: data.sourceId,
          sourceLabel: liveAudioSourceLabel || "Participant",
          participantRole: liveAudioRole || "member",
          expiresAt: data.expiresAt,
          joinUrl,
        },
        ...prev,
      ]);

      toast({
        title: "Live Audio Link Issued",
        description: `Created join link for ${liveAudioSourceLabel || "Participant"}`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Live Audio Link Error",
        description: error.message || "Unable to issue link",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLiveAudioBusy(false);
    }
  }, [isAuthenticated, liveAudioRunId, liveAudioSourceLabel, liveAudioRole, liveAudioExpiresMinutes, toast]);

  const handleFinalizeLiveAudio = useCallback(async () => {
    if (!isAuthenticated || !liveAudioRunId) return;

    setLiveAudioBusy(true);
    try {
      const response = await fetch("/live-audio/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ runId: liveAudioRunId }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || "Unable to finalize live audio");
      }

      toast({
        title: "Live Audio Finalized",
        description: `Finalized ${data.chunkCount || 0} chunks across ${data.sourceCount || 0} sources`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      await fetchLiveAudioStatus();
    } catch (error) {
      toast({
        title: "Finalize Error",
        description: error.message || "Unable to finalize live audio",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLiveAudioBusy(false);
    }
  }, [isAuthenticated, liveAudioRunId, fetchLiveAudioStatus, toast]);

  const handleCopyLiveAudioLink = useCallback(async (joinUrl) => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast({
        title: "Link Copied",
        description: "Participant join link copied to clipboard",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Could not copy link automatically",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
    }
  }, [toast]);
  
  // Track access after successful authentication
  useEffect(() => {
    if (isAuthenticated && shareToken?.id) {
      // Track this access
      trackAccess({ tokenId: shareToken.id });
    }
  }, [isAuthenticated, shareToken?.id, trackAccess]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordInput.trim()) {
      setPasswordError("Please enter a password");
      return;
    }
    if (!emailInput.trim() || !emailInput.includes("@")) {
      setPasswordError("Please enter a valid email address");
      return;
    }
    
    try {
      console.log("Validating password for token:", tokenParam);
      // Validate password against stored hash
      const result = await validatePassword({
        token: tokenParam,
        password: passwordInput
      });
      
      console.log("Password validation result:", result);
      
      if (result?.valid === true || result?.data?.valid === true) {
        console.log("Password validation successful");
        setIsAuthenticated(true);
        setUserEmail(emailInput);
        setPasswordError("");
      } else {
        console.log("Password validation failed:", result);
        setPasswordError(result?.message || result?.data?.message || "Invalid access code. Please try again.");
      }
    } catch (err) {
      console.error("Error validating password:", err);
      setPasswordError("Error validating password. Please try again.");
    }
  };

  // Screenshot detection
  useEffect(() => {
    if (!isAuthenticated || !shareToken) return;

    const handleScreenshotAttempt = async () => {
      try {
        await logScreenshot({
          tokenId: shareToken.id,
          userEmail: userEmail,
          creatorEmail: shareToken.createdBy?.email || `${shareToken.createdBy?.firstName} ${shareToken.createdBy?.lastName}`,
          scenarioTitle: shareToken.scenario?.title,
        });
      } catch (error) {
        console.error("Failed to log screenshot attempt:", error);
      }
    };

    // Keyboard shortcuts for screenshots
    const handleKeyDown = (e) => {
      // Mac: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
      // Windows: PrtScn, Windows+Shift+S
      if (
        (e.metaKey && e.shiftKey && (e.key === "3" || e.key === "4" || e.key === "5")) ||
        (e.key === "PrintScreen") ||
        (e.metaKey && e.shiftKey && e.key === "s" && navigator.platform.includes("Win"))
      ) {
        handleScreenshotAttempt();
        toast({
          title: "Screenshot Attempt Detected",
          description: "This action has been logged and the instructor has been notified.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    // Block right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      handleScreenshotAttempt();
      toast({
        title: "Action Blocked",
        description: "Right-click is disabled on this page.",
        status: "warning",
        duration: 3000,
      });
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [isAuthenticated, shareToken, userEmail, logScreenshot, toast]);

  const handleCheckboxChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedStudents = useMemo(() => {
    if (!cohortStudents?.length) return [];
    return cohortStudents.filter((s) => selectedStudentIds.includes(s.id));
  }, [cohortStudents, selectedStudentIds]);

  useEffect(() => {
    if (!isAuthenticated || selectedStudentIds.length > 0) return;

    const seededStudents = shareToken?.simulationSession?.scenarioRun?.students;
    const candidateIds = Array.isArray(seededStudents)
      ? seededStudents
          .map((entry) => {
            if (!entry) return null;
            if (typeof entry === "string") return entry;
            if (typeof entry === "object") return entry.id || entry.studentId || entry.userId || null;
            return null;
          })
          .filter(Boolean)
      : [];

    if (candidateIds.length === 0) return;

    const validIds = candidateIds.filter((id) => cohortStudents?.some((student) => student.id === id));
    if (validIds.length === 0) return;

    setSelectedStudentIds(validIds);
    const primaryId = validIds[0];
    const primaryStudent = cohortStudents?.find((student) => student.id === primaryId);
    handleInputChange("studentId", primaryId || "");
    handleInputChange(
      "studentName",
      primaryStudent ? `${primaryStudent.firstName || ""} ${primaryStudent.lastName || ""}`.trim() : ""
    );

    const metadata = shareToken?.simulationSession?.scenarioRun?.runMetadata || {};
    const metadataLeaderId = metadata?.teamLeaderId || metadata?.teamLeadId || metadata?.leaderId || null;
    setTeamLeaderId(metadataLeaderId && validIds.includes(metadataLeaderId) ? metadataLeaderId : primaryId || "");
  }, [
    isAuthenticated,
    selectedStudentIds.length,
    shareToken?.simulationSession?.scenarioRun?.students,
    shareToken?.simulationSession?.scenarioRun?.runMetadata,
    cohortStudents,
    handleInputChange,
  ]);

  const handleStudentSelectionChange = useCallback((e) => {
    const ids = Array.from(e.target.selectedOptions || []).map((option) => option.value).filter(Boolean);
    setSelectedStudentIds(ids);
    const primaryId = ids[0] || "";
    const primary = cohortStudents?.find((s) => s.id === primaryId);
    handleInputChange("studentId", primaryId);
    handleInputChange("studentName", primary ? `${primary.firstName || ""} ${primary.lastName || ""}`.trim() : "");
    if (!primaryId || !ids.includes(teamLeaderId)) {
      setTeamLeaderId(primaryId || "");
    }
  }, [cohortStudents, handleInputChange, teamLeaderId]);

  const getEvaluationFormTitle = useCallback((formKey) => {
    if (formKey === "nremt_medical") return "NREMT Medical Evaluation";
    if (formKey === "nremt_trauma") return "NREMT Trauma Evaluation";
    if (formKey === "nremt_integrated_ooh") return "NREMT Integrated OOH Evaluation";
    if (formKey === "ph4456") return "PH-4456 Clinical Evaluation";
    return "Clinical Evaluation";
  }, []);

  const handleEvaluationFormChange = useCallback(async (newForm) => {
    if (!tokenParam || !accessCodeInput || !newForm || newForm === shareToken?.simulationSession?.evaluationForm) {
      return;
    }

    setEvalFormUpdating(true);
    try {
      const response = await fetch("/scenario-share-token/update-evaluation-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: tokenParam,
          accessCode: accessCodeInput,
          evaluationForm: newForm,
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to update evaluation form");
      }

      setShareToken((prev) => ({
        ...prev,
        simulationSession: {
          ...(prev?.simulationSession || {}),
          evaluationForm: newForm,
        },
      }));

      toast({
        title: "Evaluation form updated",
        description: `Using ${getEvaluationFormTitle(newForm)} for this run.`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error.message || "Could not update evaluation form",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setEvalFormUpdating(false);
    }
  }, [tokenParam, accessCodeInput, shareToken?.simulationSession?.evaluationForm, toast, getEvaluationFormTitle]);

  const renderEvaluationFormSwitcher = () => {
    const isInstructorRun = shareToken?.simulationSession?.sessionType === "standard_evaluation";
    if (!isInstructorRun) return null;

    const currentForm = shareToken?.simulationSession?.evaluationForm || "ph4456";
    const options = availableEvaluationForms.length
      ? availableEvaluationForms
      : [
          { value: "ph4456", label: "PH-4456 Clinical Evaluation" },
          { value: "nremt_medical", label: "NREMT Medical Evaluation" },
          { value: "nremt_trauma", label: "NREMT Trauma Evaluation" },
          { value: "nremt_integrated_ooh", label: "NREMT Integrated OOH Evaluation" },
        ];

    return (
      <HStack spacing={2} align="center">
        <Text fontSize="sm" color="gray.400">Eval Form</Text>
        <Select
          size="sm"
          maxW="290px"
          value={currentForm}
          onChange={(e) => handleEvaluationFormChange(e.target.value)}
          isDisabled={evalFormUpdating || templatesFetching}
          bg="gray.700"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </HStack>
    );
  };

  const renderStudentAssignmentSection = () => (
    <Accordion allowToggle defaultIndex={[0]}>
      <AccordionItem borderColor="gray.700">
        <AccordionButton px={0}>
          <Box flex="1" textAlign="left">
            <Heading size="md">Student/Team Assignment</Heading>
            <Text fontSize="sm" color="gray.400" mt={1}>
              Select one or more students and designate team leader when needed.
            </Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={0}>
          <VStack spacing={4} align="stretch">
            {isNoCohortMode ? (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Student Name</FormLabel>
                  <Input
                    value={manualStudentName}
                    onChange={(e) => setManualStudentName(e.target.value)}
                    placeholder="Enter student name"
                    bg="gray.700"
                  />
                </FormControl>
                <Text fontSize="sm" color="gray.500">
                  This evaluation session has no cohort. Enter student name above.
                  {sendToStudent && " Student email is required when sending."}
                </Text>
              </VStack>
            ) : (
              <>
                <FormControl isRequired>
                  <FormLabel>Select Student(s)</FormLabel>
                  {fetchingStudents ? (
                    <Spinner size="sm" />
                  ) : cohortStudents && cohortStudents.length > 0 ? (
                    <Select
                      value={selectedStudentIds}
                      onChange={handleStudentSelectionChange}
                      multiple
                      size={Math.min(10, Math.max(4, cohortStudents.length))}
                      bg="gray.700"
                    >
                      {cohortStudents.map((student, index) => {
                        const displayName = student.firstName && student.lastName
                          ? `${student.lastName}, ${student.firstName}`
                          : student.email;
                        const isInstructor = student.primaryRole === "instructor";
                        return (
                          <React.Fragment key={student.id}>
                            {index > 0 &&
                              !cohortStudents[index - 1]?.primaryRole?.includes("instructor") &&
                              isInstructor && <option disabled>──────────</option>}
                            <option value={student.id}>
                              {displayName}
                              {isInstructor ? " (Instructor)" : ""}
                              {submittedStudents.has(student.id) ? " ✓" : ""}
                            </option>
                          </React.Fragment>
                        );
                      })}
                    </Select>
                  ) : (
                    <Text color="gray.400" fontSize="sm">
                      No students found in this cohort. Add students to the cohort first.
                    </Text>
                  )}
                </FormControl>
                {selectedStudentIds.length > 1 && (
                  <FormControl isRequired>
                    <FormLabel>Team Leader</FormLabel>
                    <Select
                      value={teamLeaderId}
                      onChange={(e) => setTeamLeaderId(e.target.value)}
                      placeholder="Select team leader"
                      bg="gray.700"
                    >
                      {selectedStudents.map((student) => {
                        const name = student.firstName && student.lastName
                          ? `${student.lastName}, ${student.firstName}`
                          : student.email;
                        return (
                          <option key={student.id} value={student.id}>
                            {name}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
                {submittedStudents.size > 0 && (
                  <Text fontSize="sm" color="green.400" mt={2}>
                    ✓ = Already evaluated ({submittedStudents.size} student{submittedStudents.size !== 1 ? "s" : ""})
                  </Text>
                )}
              </>
            )}

            {shareToken?.allowEmailToStudent && (
              <Box bg="blue.900" p={4} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <Checkbox
                    isChecked={sendToStudent}
                    onChange={(e) => {
                      setSendToStudent(e.target.checked);
                      if (e.target.checked && !isNoCohortMode && selectedStudents.length > 0 && selectedStudents[0]?.email) {
                        setStudentEmailForEval(selectedStudents[0].email);
                      }
                    }}
                    colorScheme="blue"
                  >
                    <Text fontWeight="semibold">Send evaluation to student via email</Text>
                  </Checkbox>

                  {sendToStudent && isNoCohortMode && (
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Student Email Address</FormLabel>
                      <Input
                        type="email"
                        value={studentEmailForEval}
                        onChange={(e) => setStudentEmailForEval(e.target.value)}
                        placeholder="Enter student's email address"
                        bg="gray.700"
                        size="sm"
                      />
                    </FormControl>
                  )}

                  {sendToStudent && !isNoCohortMode && selectedStudentIds.length > 0 && (
                    <Text fontSize="sm" color="blue.300">
                      Will be sent to selected students with emails on file
                    </Text>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );

  const renderCriticalCriteriaSection = () => (
    <Box>
      <Heading size="md" mb={3}>Critical Criteria</Heading>
      <VStack align="stretch" spacing={2}>
        {Object.entries((() => {
          const formType = shareToken?.simulationSession?.evaluationForm;
          if (formType === "nremt_integrated_ooh") {
            return {
              mandatoryActionsFailure: "Failure to appropriately address mandatory actions",
              failureCompetentParamedic: "Failure to manage patient as competent Paramedic",
              unacceptableAffect: "Exhibits unacceptable affect",
              dangerousIntervention: "Uses/orders dangerous or inappropriate intervention",
            };
          }
          if (formType === "nremt_trauma") {
            return {
              failureToInitiateTransportWithin10Minutes: "Failure to initiate/call for transport within 10 minutes",
              failureToTakeOrVerbalizePpe: "Failure to take or verbalize PPE precautions",
              failureToDetermineSceneSafety: "Failure to determine scene safety",
              failureToAssessSpinalProtection: "Failure to assess/provide spinal protection when indicated",
              failureToProvideHighConcentrationOxygen: "Failure to provide high concentration oxygen",
              failureToAssessOrProvideAdequateVentilation: "Failure to assess/provide adequate ventilation",
              failureToManageAbcProblems: "Failure to manage airway/breathing/hemorrhage/shock problems",
              failureToDifferentiateImmediateTransportNeed: "Failure to differentiate immediate transport need",
              detailedHistoryBeforeAbc: "Detailed history/physical before ABC threats addressed",
              failureToManageCompetentEmt: "Failure to manage patient as competent EMT",
              unacceptableAffect: "Exhibits unacceptable affect",
              dangerousOrInappropriateIntervention: "Uses/orders dangerous or inappropriate intervention",
            };
          }
          return {
            failureToInitiateTransportWithin15Minutes: "Failure to initiate or call for transport within 15 minutes",
            failureToTakeOrVerbalizePpe: "Failure to take or verbalize appropriate PPE precautions",
            failureToDetermineSceneSafety: "Failure to determine scene safety before approaching patient",
            failureToProvideAppropriateOxygen: "Failure to voice and provide appropriate oxygen therapy",
            failureToAssessOrProvideAdequateVentilation: "Failure to assess/provide adequate ventilation",
            failureToManageAbcProblems: "Failure to manage airway, breathing, hemorrhage, or shock problems",
            failureToDifferentiateImmediateTransportNeed: "Failure to differentiate immediate transport need",
            detailedHistoryBeforeAbc: "Performed detailed history/physical before ABC threats addressed",
            failureToDeterminePrimaryProblem: "Failure to determine the patient's primary problem",
            dangerousOrInappropriateIntervention: "Ordered a dangerous or inappropriate intervention",
            failureToProvideSpinalProtectionWhenIndicated: "Failure to provide spinal protection when indicated",
          };
        })()).map(([key, label]) => (
          <Checkbox
            key={key}
            isChecked={criticalFailures[key]}
            onChange={(e) => setCriticalFailures((prev) => ({ ...prev, [key]: e.target.checked }))}
          >
            {label}
          </Checkbox>
        ))}
      </VStack>
    </Box>
  );

  const renderScenarioTrackingSection = () => {
    const getTrackingCounts = (items, groupKey, includeInterventionMeta = false) => {
      const total = items.length;
      const performed = items.filter((item) => {
        if (includeInterventionMeta) {
          return !!scenarioTrackingChecks[groupKey][item.id] || isAutoCheckedIntervention(item.label);
        }
        return !!scenarioTrackingChecks[groupKey][item.id];
      }).length;
      const remaining = Math.max(total - performed, 0);
      return { performed, total, remaining };
    };

    const renderTrackingHeading = (title, items, groupKey, includeInterventionMeta = false) => {
      const counts = getTrackingCounts(items, groupKey, includeInterventionMeta);
      const colorScheme = counts.remaining === 0 ? "green" : counts.performed > 0 ? "yellow" : "red";
      return (
        <HStack justify="space-between" mb={2} wrap="wrap">
          <Text fontWeight="semibold">{title}</Text>
          <HStack spacing={2}>
            <Badge colorScheme={colorScheme}>
              {counts.performed}/{counts.total}
            </Badge>
            <Text fontSize="xs" color={counts.remaining === 0 ? "green.300" : "orange.300"}>
              {counts.remaining} remaining
            </Text>
          </HStack>
        </HStack>
      );
    };

    const renderTrackingList = (items, groupKey, includeInterventionMeta = false) => (
      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))", "2xl": "repeat(3, minmax(0, 1fr))" }} gap={2}>
        {items.map((item) => {
          const autoChecked = includeInterventionMeta ? isAutoCheckedIntervention(item.label) : false;
          const checked = includeInterventionMeta
            ? !!scenarioTrackingChecks[groupKey][item.id] || autoChecked
            : !!scenarioTrackingChecks[groupKey][item.id];
          return (
            <HStack key={item.id} spacing={2} align="start" p={2} bg="gray.700" borderRadius="md">
              <Checkbox
                isChecked={checked}
                onChange={() => toggleScenarioTracking(groupKey, item.id)}
                mt={0.5}
              />
              <VStack align="start" spacing={0} flex={1}>
                <Text as="span" fontSize="sm" color="gray.300" title={item.label}>
                  {includeInterventionMeta ? `[${item.group}] ${item.shortLabel || item.label}` : item.label}
                </Text>
                {includeInterventionMeta && autoChecked && (
                  <Badge colorScheme="green" size="sm">Auto</Badge>
                )}
              </VStack>
            </HStack>
          );
        })}
      </Grid>
    );

    return (
      <Box>
        <Accordion allowToggle defaultIndex={[0]}>
          <AccordionItem borderColor="gray.700">
            <AccordionButton px={0}>
              <Box flex="1" textAlign="left">
                <Heading size="md">Scenario Tracking (Record Only)</Heading>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  These checkfields are for audit/tracking and do not change form scoring.
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={0}>
              <VStack align="stretch" spacing={4}>
                {scenarioTrackingItems.primaryAssessment.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Primary Assessment Fields", scenarioTrackingItems.primaryAssessment, "primaryAssessment")}
                    {renderTrackingList(scenarioTrackingItems.primaryAssessment, "primaryAssessment")}
                  </Box>
                )}
                {scenarioTrackingItems.secondaryAssessment.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Secondary Assessment Fields", scenarioTrackingItems.secondaryAssessment, "secondaryAssessment")}
                    {renderTrackingList(scenarioTrackingItems.secondaryAssessment, "secondaryAssessment")}
                  </Box>
                )}
                {scenarioTrackingItems.patientHistory.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Patient History/HPI Fields", scenarioTrackingItems.patientHistory, "patientHistory")}
                    {renderTrackingList(scenarioTrackingItems.patientHistory, "patientHistory")}
                  </Box>
                )}
                {scenarioTrackingItems.interventions.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Interventions", scenarioTrackingItems.interventions, "interventions", true)}
                    {renderTrackingList(scenarioTrackingItems.interventions, "interventions", true)}
                  </Box>
                )}
                {scenarioTrackingItems.criticalActions.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Critical Actions", scenarioTrackingItems.criticalActions, "criticalActions")}
                    {renderTrackingList(scenarioTrackingItems.criticalActions, "criticalActions")}
                  </Box>
                )}
                {scenarioTrackingItems.pitfalls.length > 0 && (
                  <Box>
                    {renderTrackingHeading("Pitfalls", scenarioTrackingItems.pitfalls, "pitfalls")}
                    {renderTrackingList(scenarioTrackingItems.pitfalls, "pitfalls")}
                  </Box>
                )}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>
    );
  };

  const renderTraumaRubricSection = () => {
    const traumaSections = [
      {
        key: "sceneSizeUp",
        title: "Scene Size-Up",
        possiblePoints: 6,
        items: [
          "Takes/verbalizes appropriate PPE precautions",
          "Determines scene/situation is safe",
          "Determines mechanism of injury/nature of illness",
          "Determines number of patients",
          "Requests additional help if necessary",
          "Considers stabilization of spine",
        ],
      },
      {
        key: "primarySurvey",
        title: "Primary Survey/Resuscitation",
        possiblePoints: 13,
        items: [
          "Verbalizes general impression",
          "Determines responsiveness/LOC",
          "Determines chief complaint/life threats",
          "Airway: opens/assesses airway",
          "Airway: inserts adjunct as indicated",
          "Breathing: assesses breathing",
          "Breathing: assures adequate ventilation",
          "Breathing: initiates oxygen therapy",
          "Breathing: manages injury compromising ventilation",
          "Circulation: checks pulse",
          "Circulation: assesses skin",
          "Circulation: controls major bleeding",
          "Circulation: initiates shock management",
          "Identifies priority patient and transport decision by GCS",
        ],
      },
      {
        key: "historyTaking",
        title: "History Taking",
        possiblePoints: 2,
        items: [
          "Obtains/directs baseline vital signs",
          "Attempts to obtain SAMPLE history",
        ],
      },
      {
        key: "secondaryAssessment",
        title: "Secondary Assessment",
        possiblePoints: 19,
        items: [
          "Head exam completed",
          "Neck exam completed",
          "Chest exam completed",
          "Abdomen/pelvis exam completed",
          "Lower extremities exam completed",
          "Upper extremities exam completed",
          "Posterior thorax/lumbar/buttocks exam completed",
          "Manages secondary injuries/wounds appropriately",
        ],
      },
      {
        key: "reassessment",
        title: "Reassessment",
        possiblePoints: 1,
        items: ["Reassesses patient"],
      },
    ];

    return (
      <Box>
        <Heading size="md" mb={3}>NREMT Trauma Structured Rubric (Total 42)</Heading>
        <VStack align="stretch" spacing={4}>
          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Actual Time Started</FormLabel>
              <Input value={traumaEval.actualStartTime} onChange={(e) => setTraumaEval((p) => ({ ...p, actualStartTime: e.target.value }))} bg="gray.700" />
            </FormControl>
            <FormControl>
              <FormLabel>Actual Time Ended</FormLabel>
              <Input value={traumaEval.actualEndTime} onChange={(e) => setTraumaEval((p) => ({ ...p, actualEndTime: e.target.value }))} bg="gray.700" />
            </FormControl>
          </HStack>
          {traumaSections.map((section) => (
            <Box key={section.key} borderWidth="1px" borderColor="gray.600" borderRadius="md" p={3}>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold">{section.title}</Text>
                <Badge colorScheme="purple">{section.possiblePoints} pts</Badge>
              </HStack>
              <VStack align="stretch" spacing={1}>
                {section.items.map((item) => (
                  <Checkbox
                    key={`${section.key}-${item}`}
                    isChecked={!!traumaEval[section.key]?.[item]}
                    onChange={(e) =>
                      setTraumaEval((p) => ({
                        ...p,
                        [section.key]: {
                          ...(p[section.key] || {}),
                          [item]: e.target.checked,
                        },
                      }))
                    }
                  >
                    {item}
                  </Checkbox>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>
    );
  };

  const renderIntegratedRubricSection = () => {
    const scoreOptions = [0, 1, 2, 3];
    const scoreRows = [
      { key: "leadershipSceneManagement", label: "Leadership and Scene Management" },
      { key: "patientAssessment", label: "Patient Assessment" },
      { key: "patientManagement", label: "Patient Management" },
      { key: "interpersonalRelations", label: "Interpersonal Relations" },
      { key: "integration", label: "Integration (Field Impression/Transport Decision)" },
    ];
    const total = scoreRows.reduce((sum, row) => sum + Number(integratedEval[row.key] || 0), 0);

    return (
      <Box>
        <Heading size="md" mb={3}>NREMT Integrated OOH Rubric (Total 15)</Heading>
        <VStack align="stretch" spacing={4}>
          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Actual Start Time</FormLabel>
              <Input value={integratedEval.actualStartTime} onChange={(e) => setIntegratedEval((p) => ({ ...p, actualStartTime: e.target.value }))} bg="gray.700" />
            </FormControl>
            <FormControl>
              <FormLabel>Actual Time Patient Transported</FormLabel>
              <Input value={integratedEval.actualTransportTime} onChange={(e) => setIntegratedEval((p) => ({ ...p, actualTransportTime: e.target.value }))} bg="gray.700" />
            </FormControl>
            <FormControl>
              <FormLabel>Actual End Time</FormLabel>
              <Input value={integratedEval.actualEndTime} onChange={(e) => setIntegratedEval((p) => ({ ...p, actualEndTime: e.target.value }))} bg="gray.700" />
            </FormControl>
          </HStack>
          {scoreRows.map((row) => (
            <FormControl key={row.key}>
              <HStack justify="space-between" mb={1}>
                <FormLabel mb={0}>{row.label}</FormLabel>
                <Badge colorScheme="blue">{integratedEval[row.key]} / 3</Badge>
              </HStack>
              <RadioGroup value={String(integratedEval[row.key])} onChange={(val) => setIntegratedEval((p) => ({ ...p, [row.key]: Number(val) }))}>
                <HStack spacing={4}>
                  {scoreOptions.map((n) => (
                    <Radio key={`${row.key}-${n}`} value={String(n)}>{n}</Radio>
                  ))}
                </HStack>
              </RadioGroup>
              {row.key === "patientManagement" && (
                <Text mt={2} fontSize="xs" color="gray.300">
                  Critical performed/total: {integratedSupportCounters.criticalPerformed}/{integratedSupportCounters.criticalTotal}
                  {" | "}Secondary performed/total: {integratedSupportCounters.secondaryPerformed}/{integratedSupportCounters.secondaryTotal}
                  {" | "}Optional performed/total: {integratedSupportCounters.optionalPerformed}/{integratedSupportCounters.optionalTotal}
                  {" | "}Pitfalls performed: {integratedSupportCounters.pitfallsPerformed}
                </Text>
              )}
              {row.key === "patientAssessment" && (
                <Text mt={2} fontSize="xs" color="gray.300">
                  Patient history performed/total: {integratedSupportCounters.patientHistoryPerformed}/{integratedSupportCounters.patientHistoryTotal}
                  {" | "}Primary survey complete: {integratedSupportCounters.primarySurveyComplete ? "✓" : "✗"}
                  {" | "}Secondary assessment performed/total: {integratedSupportCounters.secondaryAssessmentPerformed}/{integratedSupportCounters.secondaryAssessmentTotal}
                </Text>
              )}
            </FormControl>
          ))}
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>Integrated score total: {total} / 15</AlertDescription>
          </Alert>
        </VStack>
      </Box>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate based on mode
    if (isNoCohortMode) {
      if (!manualStudentName.trim()) {
        toast({
          title: "Student name required",
          description: "Please enter a student name",
          status: "error",
          duration: 3000
        });
        return;
      }
      // Validate email if sending to student
      if (sendToStudent && !studentEmailForEval.trim()) {
        toast({
          title: "Student email required",
          description: "Please enter student email to send evaluation",
          status: "error",
          duration: 3000
        });
        return;
      }
    } else {
      if (!selectedStudentIds.length) {
        toast({
          title: "Student required",
          description: "Please select one or more students to evaluate",
          status: "error",
          duration: 3000
        });
        return;
      }
      if (selectedStudentIds.length > 1 && !teamLeaderId) {
        toast({
          title: "Team leader required",
          description: "Please select a team leader when evaluating multiple students",
          status: "error",
          duration: 3000
        });
        return;
      }
      // Validate student has email if sending
      if (sendToStudent) {
        const missingEmail = selectedStudents.some((s) => !s?.email);
        if (missingEmail) {
          toast({
            title: "No email on file",
            description: "One or more selected students have no email address. Cannot send evaluation.",
            status: "error",
            duration: 3000
          });
          return;
        }
      }
    }

    const completionTimeMinutes = Math.floor((new Date() - startTime) / 60000);
    const evaluationTargets = isNoCohortMode
      ? [{ id: null, name: manualStudentName, email: studentEmailForEval }]
      : selectedStudents.map((s) => ({
          id: s.id,
          name: `${s.firstName || ""} ${s.lastName || ""}`.trim(),
          email: s.email || "",
        }));
    const teamLeader = !isNoCohortMode
      ? (cohortStudents?.find((s) => s.id === teamLeaderId) || null)
      : null;
    const trackingSnapshot = {
      primaryAssessment: scenarioTrackingItems.primaryAssessment.map((item) => ({
        id: item.id,
        label: item.label,
        checked: !!scenarioTrackingChecks.primaryAssessment[item.id],
      })),
      secondaryAssessment: scenarioTrackingItems.secondaryAssessment.map((item) => ({
        id: item.id,
        label: item.label,
        checked: !!scenarioTrackingChecks.secondaryAssessment[item.id],
      })),
      patientHistory: scenarioTrackingItems.patientHistory.map((item) => ({
        id: item.id,
        label: item.label,
        checked: !!scenarioTrackingChecks.patientHistory[item.id],
      })),
      interventions: scenarioTrackingItems.interventions.map((item) => ({
        id: item.id,
        group: item.group,
        shortLabel: item.shortLabel,
        label: item.label,
        autoChecked: isAutoCheckedIntervention(item.label),
        checked: !!scenarioTrackingChecks.interventions[item.id] || isAutoCheckedIntervention(item.label),
      })),
      criticalActions: scenarioTrackingItems.criticalActions.map((item) => ({
        id: item.id,
        label: item.label,
        checked: !!scenarioTrackingChecks.criticalActions[item.id],
      })),
      pitfalls: scenarioTrackingItems.pitfalls.map((item) => ({
        id: item.id,
        label: item.label,
        checked: !!scenarioTrackingChecks.pitfalls[item.id],
      })),
    };
    const evaluationFormType = shareToken?.simulationSession?.evaluationForm || "ph4456";
    const integratedTotalScore =
      Number(integratedEval.leadershipSceneManagement || 0) +
      Number(integratedEval.patientAssessment || 0) +
      Number(integratedEval.patientManagement || 0) +
      Number(integratedEval.interpersonalRelations || 0) +
      Number(integratedEval.integration || 0);
    const nremtTemplateHeader = {
      possiblePoints: evaluationFormType === "nremt_trauma" ? 42 : evaluationFormType === "nremt_integrated_ooh" ? 15 : 48,
      scenarioTitle: shareToken?.scenario?.title || "",
      actualTimeStarted: startTime?.toISOString(),
      actualTimeEnded: new Date().toISOString(),
      pointsAwarded: evaluationFormType === "nremt_integrated_ooh" ? integratedTotalScore : formData.overallScore,
    };

    try {
      let createdCount = 0;
      for (const target of evaluationTargets) {
        const result = await submit({
          sceneSize: formData.sceneSize,
          bsiPrecautions: formData.bsiPrecautions,
          sceneHazards: formData.sceneHazards,
          mechanismOfInjury: formData.mechanismOfInjury,
          numberOfPatients: formData.numberOfPatients,
          needForResources: formData.needForResources,
          generalImpression: formData.generalImpression,
          levelOfConsciousness: formData.levelOfConsciousness,
          chiefComplaint: formData.chiefComplaint,
          airwayPatency: formData.airwayPatency,
          breathingAdequacy: formData.breathingAdequacy,
          circulationAssessment: formData.circulationAssessment,
          priorityDecision: formData.priorityDecision,
          historyPresent: formData.historyPresent,
          opqrst: formData.opqrst,
          sample: formData.sample,
          pertinentNegatives: formData.pertinentNegatives,
          pertinentPositives: formData.pertinentPositives,
          vitalSigns: formData.vitalSigns,
          physicalExam: formData.physicalExam,
          diagnosticTools: formData.diagnosticTools,
          interventionRepeated: formData.interventionRepeated,
          vitalSignsTrend: formData.vitalSignsTrend,
          overallScore: formData.overallScore,
          comments: formData.comments || "",
          completionTime: completionTimeMinutes,
          formVersion: "1.0",
          criticalFailures: criticalFailures,
          evaluationDate: new Date().toISOString(),
          status: "completed",
          scenario: { _link: shareToken.scenario.id },
          cohort: cohortId ? { _link: cohortId } : null,
          createdBy: { _link: shareToken.createdBy.id },
          instructor: { _link: shareToken.createdBy.id },
          simulationSession: { _link: shareToken.simulationSession.id },
          student: isNoCohortMode ? null : { _link: target.id },
          shareToken: { _link: shareToken.id },
          studentEmail: isNoCohortMode ? studentEmailForEval : (target.email || ""),
          instructorEmail: shareToken.createdBy?.email || "",
          submittedViaEmail: null,
          emailedToStudent: sendToStudent,
          evaluationMetadata: {
            nremtTemplateHeader,
            nremtEvaluationState: nremtEval,
            traumaEvaluationState: traumaEval,
            integratedEvaluationState: integratedEval,
            criticalCriteriaChecks: criticalFailures,
            scenarioTrackingChecks: trackingSnapshot,
            integratedSupportCounters,
            multiStudentSubmission: {
              enabled: !isNoCohortMode && evaluationTargets.length > 1,
              selectedStudentIds: !isNoCohortMode ? selectedStudentIds : [],
              teamLeaderId: teamLeader?.id || null,
              teamLeaderName: teamLeader ? `${teamLeader.firstName || ""} ${teamLeader.lastName || ""}`.trim() : null,
            },
          },
        });
        if (result?.data) {
          createdCount += 1;
        }
      }

      if (createdCount > 0) {
        if (!isNoCohortMode) {
          setSubmittedStudents((prev) => new Set([...prev, ...selectedStudentIds]));
        }

        // Reset form to initial state
        setFormData({
          sceneSize: false,
          bsiPrecautions: false,
          sceneHazards: false,
          mechanismOfInjury: false,
          numberOfPatients: false,
          needForResources: false,
          generalImpression: false,
          levelOfConsciousness: false,
          chiefComplaint: false,
          airwayPatency: false,
          breathingAdequacy: false,
          circulationAssessment: false,
          priorityDecision: false,
          historyPresent: false,
          opqrst: false,
          sample: false,
          pertinentNegatives: false,
          pertinentPositives: false,
          vitalSigns: false,
          physicalExam: false,
          diagnosticTools: false,
          interventionRepeated: false,
          vitalSignsTrend: false,
          overallScore: 0,
          comments: "",
          completionTime: 0,
          studentId: "",
          studentName: "",
        });
        
        // Reset email options
        setSendToStudent(false);
        setStudentEmailForEval("");
        setSelectedStudentIds([]);
        setTeamLeaderId("");
        
        toast({
          title: "Evaluation submitted!",
          description: sendToStudent
            ? `Saved ${createdCount} evaluation${createdCount !== 1 ? "s" : ""} and emailed selected student(s)`
            : `Saved ${createdCount} evaluation${createdCount !== 1 ? "s" : ""}`,
          status: "success",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error("Error submitting evaluation:", error);
      toast({
        title: "Submission failed",
        description: error.message || "Failed to submit evaluation",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Loading state
  if (tokenFetching) {
    return (
      <Container maxW="container.md" py={10}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading scenario...</Text>
        </VStack>
      </Container>
    );
  }

  // Error state
  if (tokenLoadError || !shareToken) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Invalid Link</AlertTitle>
          <AlertDescription>
            This scenario link is invalid or has expired. Please contact your instructor for a new link.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  // Access code protection
  if (!isAuthenticated) {
    const timeRemaining = shareToken.expiresAt 
      ? Math.max(0, Math.floor((new Date(shareToken.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
      : null;
    const usesRemaining = shareToken.maxUses 
      ? Math.max(0, shareToken.maxUses - shareToken.currentUses)
      : null;
    
    return (
      <>
        <style>{`
          @media print {
            body {
              display: none !important;
            }
          }
        `}</style>
        <Container maxW="container.sm" py={20}>
          <Box bg="gray.800" p={8} borderRadius="lg" boxShadow="xl">
            <VStack spacing={6}>
              <Heading size="lg">Access Code Required</Heading>
              {shareToken?.scenario?.title && (
                <Text color="gray.300" fontSize="lg" fontWeight="semibold">
                  {shareToken.scenario.title}
                </Text>
              )}
              <Text color="gray.400">Enter the access code to view this scenario.</Text>
              
              {/* Show usage/expiration info */}
              {(timeRemaining !== null || usesRemaining !== null) && (
                <VStack spacing={2} width="full">
                  {timeRemaining !== null && (
                    <Alert status={timeRemaining < 7 ? "warning" : "info"} borderRadius="md">
                      <AlertIcon />
                      <Box flex="1">
                        <Text fontSize="sm">
                          {timeRemaining > 0 
                            ? `Expires in ${timeRemaining} day${timeRemaining !== 1 ? 's' : ''}`
                            : "Expires today"
                          }
                        </Text>
                      </Box>
                    </Alert>
                  )}
                  {usesRemaining !== null && (
                    <Alert status={usesRemaining < 5 ? "warning" : "info"} borderRadius="md">
                      <AlertIcon />
                      <Box flex="1">
                        <Text fontSize="sm">
                          {usesRemaining} use{usesRemaining !== 1 ? 's' : ''} remaining
                        </Text>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              )}
              
              <form onSubmit={handleAccessCodeSubmit} style={{ width: "100%" }} autoComplete="off">
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Access Code</FormLabel>
                    <Input
                      type="text"
                      name="scenario-access-code"
                      id="scenario-access-code"
                      value={accessCodeInput}
                      onChange={(e) => setAccessCodeInput(e.target.value)}
                      placeholder="Enter access code"
                      bg="gray.700"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                    />
                  </FormControl>
                  {passwordError && (
                    <Alert status="error">
                      <AlertIcon />
                      {passwordError}
                    </Alert>
                  )}
                  <Button type="submit" colorScheme="blue" width="full">
                    Access Scenario
                  </Button>
                </VStack>
              </form>
            </VStack>
          </Box>
        </Container>
      </>
    );
  }

  // Check token validity
  if (!isTokenValid()) {
    const isExpired = shareToken.expiresAt && new Date(shareToken.expiresAt) < new Date();
    const isMaxUsesReached = shareToken.maxUses && shareToken.currentUses >= shareToken.maxUses;
    
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>
            {isMaxUsesReached ? "Link Limit Reached" : "Link Expired"}
          </AlertTitle>
          <AlertDescription>
            {isMaxUsesReached 
              ? `This scenario link has reached its maximum uses (${shareToken.maxUses}). Please contact your instructor for a new link.`
              : "This scenario link has expired or is no longer active. Please contact your instructor for a new link."
            }
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  // Check if scenario data exists before attempting to render
  if (!shareToken?.scenario || !shareToken?.scenario?.scenarioOutput || typeof shareToken.scenario.scenarioOutput !== 'object') {
    console.error('Scenario data missing or invalid:', shareToken);
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Incomplete Scenario Data</AlertTitle>
          <AlertDescription>
            The scenario data is incomplete or missing. Please try refreshing the page or contact your instructor for a new link.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  // Main evaluation form
  return (
    <>
      {/* Disable printing via CSS */}
      <style>{`
        @media print {
          body {
            display: none !important;
          }
        }
        .print-button, .download-button {
          display: none !important;
        }
        
        /* Simple Print Mode - ON SCREEN */
        body.simple-print .no-print {
          display: none !important;
          visibility: hidden !important;
          position: absolute !important;
          left: -99999px !important;
          height: 0 !important;
          overflow: hidden !important;
        }

        body.simple-print * {
          background: white !important;
          color: black !important;
          box-shadow: none !important;
        }

        body.simple-print .chakra-heading,
        body.simple-print .chakra-text,
        body.simple-print p,
        body.simple-print span,
        body.simple-print div {
          color: black !important;
        }

        body.simple-print .chakra-card,
        body.simple-print .chakra-stack,
        body.simple-print .chakra-box {
          background: white !important;
        }

        body.simple-print .chakra-card {
          border: 1px solid #000 !important;
          padding: 1px !important;
          margin: 2px 0 !important;
          page-break-inside: avoid;
        }

        body.simple-print .chakra-card__header {
          border-bottom: 1px solid #000 !important;
          padding: 2px 3px !important;
          font-size: 7pt !important;
          font-weight: bold !important;
        }

        body.simple-print h1, body.simple-print h2 {
          font-size: 8pt !important;
          margin: 2px 0 !important;
          font-weight: bold !important;
        }

        body.simple-print .watermark {
          display: none !important;
        }

        /* Accessibility Mode */
        body.accessibility-mode,
        body.accessibility-mode * {
          background: black !important;
          color: yellow !important;
          font-family: 'Courier New', monospace !important;
          font-size: 18pt !important;
          line-height: 1.8 !important;
        }

        body.accessibility-mode .chakra-card {
          border: 3px solid yellow !important;
          padding: 20px !important;
          margin: 10px 0 !important;
        }

        body.accessibility-mode .chakra-heading {
          color: yellow !important;
          font-size: 24pt !important;
          text-decoration: underline !important;
        }

        body.accessibility-mode .chakra-button {
          background: yellow !important;
          color: black !important;
          border: 3px solid yellow !important;
          font-weight: bold !important;
        }
      `}</style>

      <Container maxW="container.xl" py={8}>
        {/* Enhanced Toolbar with View Controls */}
        <VStack spacing={4} mb={6} align="stretch">
          {/* Warning Banner */}
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Confidential Scenario</AlertTitle>
              <AlertDescription>
                This scenario is confidential. Screenshots and printing are disabled and monitored. All access attempts are logged.
              </AlertDescription>
            </Box>
          </Alert>
          
          {/* View Mode Controls Toolbar */}
          <HStack 
            spacing={4} 
            p={4} 
            bg="gray.800" 
            borderRadius="md" 
            justify="space-between"
            className="no-print"
          >
            <HStack spacing={3}>
              <Text fontSize="sm" color="gray.400" fontWeight="semibold">View Mode:</Text>
              <Button
                size="sm"
                colorScheme={viewMode === 'tablet' ? 'blue' : 'gray'}
                onClick={handleTabletView}
                leftIcon={<Text>📱</Text>}
              >
                {viewMode === 'tablet' ? 'Exit Tablet View' : 'Tablet View'}
              </Button>
              <Button
                size="sm"
                colorScheme={viewMode === 'accessibility' ? 'green' : 'gray'}
                onClick={handleAccessibilityView}
                leftIcon={<Text>♿</Text>}
              >
                {viewMode === 'accessibility' ? 'Exit Accessible View' : 'Accessible View'}
              </Button>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleSimplePrint}
                leftIcon={<Text>🖨️</Text>}
              >
                Simple Print
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                onClick={handleDownloadPDF}
                leftIcon={<Text>📄</Text>}
              >
                Download PDF
              </Button>
            </HStack>
          </HStack>
          
          {/* Tablet view info */}
          {viewMode === 'tablet' && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                Tablet view active: Optimized layout for handheld devices. Swipe or use tabs to switch between scenario and evaluation.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Accessibility view info */}
          {viewMode === 'accessibility' && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                Accessible view active: High-contrast yellow-on-black display with larger text and enhanced readability for improved accessibility.
              </AlertDescription>
            </Alert>
          )}
        </VStack>

        {isAuthenticated && liveAudioRunId && (
          <Box bg="gray.800" p={5} borderRadius="lg" mb={6} className="no-print">
            <Accordion allowToggle defaultIndex={[]}>
              <AccordionItem border="none">
                <AccordionButton px={0}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md">Multi-Input Live Audio Intake</Heading>
                    <Text fontSize="sm" color="gray.400">
                      Issue participant links, monitor chunk processing, and finalize transcripts for this scenario run.
                    </Text>
                    <Text fontSize="xs" color="gray.500">Run ID: {liveAudioRunId}</Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0} pb={0}>
                  <VStack spacing={4} align="stretch">
                    <HStack justify="space-between" wrap="wrap">
                      <Box />
                      <HStack>
                        <Button size="sm" onClick={fetchLiveAudioStatus} isLoading={liveAudioBusy}>
                          Refresh Status
                        </Button>
                        <Button size="sm" colorScheme="orange" onClick={handleFinalizeLiveAudio} isLoading={liveAudioBusy}>
                          Finalize Audio
                        </Button>
                      </HStack>
                    </HStack>

                    <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr auto" }} gap={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Participant Label</FormLabel>
                        <Input
                          value={liveAudioSourceLabel}
                          onChange={(e) => setLiveAudioSourceLabel(e.target.value)}
                          placeholder="Participant name or team role"
                          bg="gray.700"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Role</FormLabel>
                        <Select value={liveAudioRole} onChange={(e) => setLiveAudioRole(e.target.value)} bg="gray.700">
                          <option value="member">Team Member</option>
                          <option value="team_lead">Team Lead</option>
                          <option value="observer">Observer</option>
                          <option value="unknown">Unknown</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Expiry (minutes)</FormLabel>
                        <NumberInput
                          min={5}
                          max={720}
                          value={liveAudioExpiresMinutes}
                          onChange={(_, valueAsNumber) => setLiveAudioExpiresMinutes(Number.isFinite(valueAsNumber) ? valueAsNumber : 180)}
                        >
                          <NumberInputField bg="gray.700" />
                        </NumberInput>
                      </FormControl>
                      <FormControl alignSelf="end">
                        <Button colorScheme="blue" onClick={handleIssueLiveAudioLink} isLoading={liveAudioBusy} width="full">
                          Issue Link
                        </Button>
                      </FormControl>
                    </Grid>

                    {liveAudioStatus?.totals && (
                      <Box p={3} borderRadius="md" bg="gray.700">
                        <HStack justify="space-between" mb={2} wrap="wrap">
                          <Text fontSize="sm">
                            Chunks: {liveAudioStatus.totals.total || 0} total, {liveAudioStatus.totals.ready || 0} ready, {liveAudioStatus.totals.failed || 0} failed
                          </Text>
                          <Badge colorScheme={liveAudioStatus.totals.progressPct >= 80 ? "green" : "yellow"}>
                            {liveAudioStatus.totals.progressPct || 0}% ready
                          </Badge>
                        </HStack>
                        <Progress value={liveAudioStatus.totals.progressPct || 0} colorScheme="green" size="sm" borderRadius="md" />
                      </Box>
                    )}

                    {liveAudioLinks.length > 0 && (
                      <VStack spacing={2} align="stretch">
                        {liveAudioLinks.map((entry) => (
                          <Box key={entry.token} p={3} borderRadius="md" bg="gray.700">
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={0} flex={1}>
                                <Text fontSize="sm" fontWeight="semibold">
                                  {entry.sourceLabel} ({entry.participantRole})
                                </Text>
                                <Text fontSize="xs" color="gray.400">
                                  Expires: {entry.expiresAt ? new Date(entry.expiresAt).toLocaleString() : "N/A"}
                                </Text>
                                <Input value={entry.joinUrl} readOnly mt={2} fontSize="xs" bg="gray.800" />
                              </VStack>
                              <Button size="sm" onClick={() => handleCopyLiveAudioLink(entry.joinUrl)}>
                                Copy
                              </Button>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        )}

        {viewMode === 'tablet' ? (
          // Tablet View - Tabbed Interface
          <Box bg="gray.800" borderRadius="lg">
            <Tabs isFitted variant="enclosed">
              <TabList mb={4}>
                <Tab>Scenario</Tab>
                <Tab>Evaluation</Tab>
              </TabList>
              <TabPanels>
                <TabPanel p={6}>
                  {/* Scenario section */}
                  <Box position="relative">
                    {/* Watermark */}
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%) rotate(-45deg)"
                      fontSize="6xl"
                      fontWeight="bold"
                      color="whiteAlpha.100"
                      pointerEvents="none"
                      zIndex={1}
                      whiteSpace="nowrap"
                      userSelect="none"
                      className="watermark"
                    >
                      ElevationEMS
                    </Box>
                    <Box position="relative" zIndex={2}>
                      {shareToken?.scenario?.scenarioOutput && (
                        <React.Suspense fallback={<Spinner size="lg" />}>
                          <ScenarioDisplay 
                            scenario={shareToken.scenario.scenarioOutput} 
                            metadata={{
                              id: shareToken.scenario.id,
                              levelOfCare: shareToken.scenario.levelOfCare,
                              mechanismNatureOfInjury: shareToken.scenario.majorSystemInvolved,
                              title: shareToken.scenario.title
                            }}
                            evaluationMode={true}
                            evaluationData={liveScoreChecks}
                            onEvaluationChange={handleLiveScoreCheck}
                            showToolbar={false}
                            hideRawInput={true}
                          />
                        </React.Suspense>
                      )}
                    </Box>
                  </Box>
                </TabPanel>
                <TabPanel p={6}>
                  {/* Evaluation form */}
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6} align="stretch">
                      <Box>
                        <HStack justify="space-between" mb={4} align="start" wrap="wrap">
                          <VStack align="start" spacing={2}>
                            <Heading size="lg">
                              {getEvaluationFormTitle(shareToken?.simulationSession?.evaluationForm)}
                            </Heading>
                            {renderEvaluationFormSwitcher()}
                          </VStack>
                          <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                            {liveScoreChecks ? Object.values(liveScoreChecks).reduce((total, category) =>
                              total + Object.values(category).filter(Boolean).length, 0
                            ) : 0} items live scored
                          </Badge>
                        </HStack>
                        
                        {/* Overall progress indicator */}
                        <Box mb={4}>
                          <Text fontSize="sm" color="gray.400" mb={2}>Overall Completion</Text>
                          <Progress 
                            value={(() => {
                              const allSections = Object.keys(nremtEval).filter(k => k !== 'overallPerformance');
                              const totalFields = allSections.reduce((sum, section) => 
                                sum + Object.keys(nremtEval[section]).filter(k => k !== 'notes').length, 0
                              );
                              const completedFields = allSections.reduce((sum, section) => 
                                sum + Object.entries(nremtEval[section])
                                  .filter(([k, v]) => k !== 'notes' && v !== 'notPerformed').length, 0
                              );
                              return totalFields > 0 ? (completedFields / totalFields) * 100 : 0;
                            })()}
                            colorScheme="green"
                            size="sm"
                            borderRadius="md"
                          />
                        </Box>
                      </Box>

                      {renderStudentAssignmentSection()}

                      {/* Evaluation Section Component */}
                      {shareToken?.simulationSession?.evaluationForm === "nremt_trauma" && renderTraumaRubricSection()}
                      {shareToken?.simulationSession?.evaluationForm === "nremt_integrated_ooh" && renderIntegratedRubricSection()}
                      {shareToken?.simulationSession?.evaluationForm !== "nremt_trauma" &&
                        shareToken?.simulationSession?.evaluationForm !== "nremt_integrated_ooh" &&
                        (() => {
                        const EvalSection = ({ title, sectionKey, fields, liveScores, evalState, onUpdate }) => {
                          const fieldKeys = Object.keys(fields).filter(k => k !== 'notes');
                          const completed = fieldKeys.filter(k => evalState[k] !== 'notPerformed').length;
                          const total = fieldKeys.length;
                          
                          return (
                            <AccordionItem>
                              <AccordionButton bg={sectionNeedsAttention(sectionKey, evalState, liveScores) ? 'red.900' : undefined}>
                                <Box flex="1" textAlign="left">
                                  <HStack>
                                    <Text fontSize="lg" fontWeight="bold" color={sectionNeedsAttention(sectionKey, evalState, liveScores) ? 'red.300' : undefined}>{title}</Text>
                                    <Badge colorScheme={completed === total ? 'green' : completed > 0 ? 'yellow' : 'gray'}>
                                      {completed}/{total}
                                    </Badge>
                                    {sectionNeedsAttention(sectionKey, evalState, liveScores) && (
                                      <Badge colorScheme="red" size="sm">Needs Performance Rating</Badge>
                                    )}
                                  </HStack>
                                </Box>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel pb={4}>
                                <VStack spacing={4} align="stretch">
                                  {fieldKeys.map((key) => {
                                    const liveValue = liveScores?.[sectionKey]?.[key];
                                    const currentValue = evalState[key];
                                    const wasLiveScored = liveValue === true;
                                    const wasModified = wasLiveScored && currentValue !== 'performed';
                                    
                                    return (
                                      <FormControl key={key}>
                                        <HStack spacing={2} mb={1}>
                                          <FormLabel mb={0} fontSize="md">{fields[key]}</FormLabel>
                                          {wasLiveScored && !wasModified && (
                                            <Badge colorScheme="green" size="sm">Live</Badge>
                                          )}
                                          {wasModified && (
                                            <Badge colorScheme="yellow" size="sm">Modified</Badge>
                                          )}
                                          {!wasLiveScored && currentValue !== 'notPerformed' && (
                                            <Badge colorScheme="gray" size="sm">Manual</Badge>
                                          )}
                                        </HStack>
                                        <RadioGroup
                                          value={currentValue}
                                          onChange={(val) => onUpdate(sectionKey, key, val)}
                                        >
                                          <Stack direction="row" spacing={6} flexWrap="wrap">
                                            <Radio value="performed" size="lg">Performed</Radio>
                                            <Radio value="notPerformed" size="lg">Not Performed</Radio>
                                            <Radio value="incorrect" size="lg">Incorrect</Radio>
                                            <Radio value="na" size="lg">N/A</Radio>
                                          </Stack>
                                        </RadioGroup>
                                      </FormControl>
                                    );
                                  })}
                                  <FormControl isRequired={completed > 0}>
                                    <HStack spacing={2} mb={1}>
                                      <FormLabel mb={0} color={!evalState.performance && completed > 0 ? 'red.400' : undefined}>
                                        Section Performance {completed > 0 && !evalState.performance && '(Required)'}
                                      </FormLabel>
                                      {!evalState.performance && completed > 0 && (
                                        <Badge colorScheme="red" size="sm">Required</Badge>
                                      )}
                                    </HStack>
                                    <RadioGroup
                                      value={evalState.performance || ''}
                                      onChange={(val) => onUpdate(sectionKey, 'performance', val || null)}
                                    >
                                      <Stack direction="row" spacing={6} flexWrap="wrap">
                                        <Radio value="Exemplary" size="lg">Exemplary</Radio>
                                        <Radio value="Exceeds Expectations" size="lg">Exceeds</Radio>
                                        <Radio value="Proficient" size="lg">Proficient</Radio>
                                        <Radio value="Near Pass" size="lg">Near Pass</Radio>
                                        <Radio value="Fail" size="lg">Fail</Radio>
                                        <Radio value="Critical Deficiency" size="lg">Critical</Radio>
                                      </Stack>
                                    </RadioGroup>
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel>Section Notes</FormLabel>
                                    <Textarea
                                      value={evalState.notes}
                                      onChange={(e) => onUpdate(sectionKey, 'notes', e.target.value)}
                                      placeholder="Additional notes for this section..."
                                      bg="gray.700"
                                      rows={2}
                                    />
                                  </FormControl>
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          );
                        };

                        const handleEvalUpdate = (section, field, value) => {
                          setNremtEval(prev => ({
                            ...prev,
                            [section]: {
                              ...prev[section],
                              [field]: value
                            }
                          }));
                        };

                        return (
                          <Accordion allowMultiple defaultIndex={[0]}>
                            <EvalSection
                              title="Scene Size-Up"
                              sectionKey="sceneSizeUp"
                              fields={fieldLabels.sceneSizeUp}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.sceneSizeUp}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="Primary Assessment"
                              sectionKey="primaryAssessment"
                              fields={fieldLabels.primaryAssessment}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.primaryAssessment}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="SAMPLE History"
                              sectionKey="sample"
                              fields={fieldLabels.sample}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.sample}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="OPQRST Assessment"
                              sectionKey="opqrst"
                              fields={fieldLabels.opqrst}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.opqrst}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="Vital Signs"
                              sectionKey="vitalSigns"
                              fields={fieldLabels.vitalSigns}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.vitalSigns}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="Physical Examination"
                              sectionKey="physicalExam"
                              fields={fieldLabels.physicalExam}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.physicalExam}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="Interventions"
                              sectionKey="interventions"
                              fields={fieldLabels.interventions}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.interventions}
                              onUpdate={handleEvalUpdate}
                            />
                            <EvalSection
                              title="Reassessment"
                              sectionKey="reassessment"
                              fields={fieldLabels.reassessment}
                              liveScores={liveScoreChecks}
                              evalState={nremtEval.reassessment}
                              onUpdate={handleEvalUpdate}
                            />
                          </Accordion>
                        );
                      })()}

                      {/* Overall Performance Section */}
                      <Box>
                        <Heading size="md" mb={4}>Overall Performance</Heading>
                        <FormControl>
                          <FormLabel>Overall Performance Rating</FormLabel>
                          <RadioGroup
                            value={nremtEval.overallPerformance.performance || ''}
                            onChange={(val) => {
                              setNremtEval(prev => ({
                                ...prev,
                                overallPerformance: {
                                  ...prev.overallPerformance,
                                  performance: val || null
                                }
                              }));
                            }}
                          >
                            <Stack direction="row" spacing={6} flexWrap="wrap">
                              <Radio value="Exemplary" size="lg">Exemplary</Radio>
                              <Radio value="Exceeds Expectations" size="lg">Exceeds Expectations</Radio>
                              <Radio value="Proficient" size="lg">Proficient</Radio>
                              <Radio value="Near Pass" size="lg">Near Pass</Radio>
                              <Radio value="Fail" size="lg">Fail</Radio>
                              <Radio value="Critical Deficiency" size="lg">Critical Deficiency</Radio>
                            </Stack>
                          </RadioGroup>
                        </FormControl>
                        <FormControl mt={4}>
                          <FormLabel>Overall Comments</FormLabel>
                          <Textarea
                            value={nremtEval.overallPerformance.instructorNotes}
                            onChange={(e) => {
                              setNremtEval(prev => ({
                                ...prev,
                                overallPerformance: {
                                  ...prev.overallPerformance,
                                  instructorNotes: e.target.value
                                }
                              }));
                            }}
                            placeholder="Overall evaluation comments..."
                            bg="gray.700"
                            rows={6}
                          />
                        </FormControl>
                      </Box>

                      <Divider />

                      {renderScenarioTrackingSection()}
                      {renderCriticalCriteriaSection()}

                      {/* Validation warning for incomplete sections */}
                      {shareToken?.simulationSession?.evaluationForm === "nremt_medical" && (() => {
                        const sectionsNeedingAttention = Object.keys(fieldLabels)
                          .filter(sectionKey => sectionNeedsAttention(sectionKey, nremtEval[sectionKey], liveScoreChecks));
                        
                        if (sectionsNeedingAttention.length > 0) {
                          return (
                            <Alert status="warning" borderRadius="md">
                              <AlertIcon />
                              <Box flex="1">
                                <AlertTitle>Incomplete Sections</AlertTitle>
                                <AlertDescription>
                                  The following sections have evaluated items but are missing performance ratings:
                                  <VStack align="start" mt={2} spacing={1}>
                                    {sectionsNeedingAttention.map(key => {
                                      const sectionTitle = 
                                        key === 'sceneSizeUp' ? 'Scene Size-Up' :
                                        key === 'primaryAssessment' ? 'Primary Assessment' :
                                        key === 'sample' ? 'SAMPLE History' :
                                        key === 'opqrst' ? 'OPQRST Assessment' :
                                        key === 'vitalSigns' ? 'Vital Signs' :
                                        key === 'physicalExam' ? 'Physical Examination' :
                                        key === 'interventions' ? 'Interventions' :
                                        key === 'reassessment' ? 'Reassessment' :
                                        key;
                                      return (
                                        <Text key={key} fontWeight="bold" fontSize="sm">
                                          • {sectionTitle}
                                        </Text>
                                      );
                                    })}
                                  </VStack>
                                </AlertDescription>
                              </Box>
                            </Alert>
                          );
                        }
                        return null;
                      })()}

                      {submitError && (
                        <Alert status="error">
                          <AlertIcon />
                          <AlertDescription>{submitError.message}</AlertDescription>
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        colorScheme="blue"
                        size="lg"
                        isLoading={submitting}
                        loadingText="Submitting..."
                      >
                        Submit Evaluation
                      </Button>
                    </VStack>
                  </form>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        ) : (
          // Desktop View - Two sections stacked
          <VStack spacing={6} align="stretch">
            {/* Scenario Section */}
            <Box bg="gray.800" p={6} borderRadius="lg" position="relative">
              {/* Watermark overlay - only over scenario section */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%) rotate(-45deg)"
                fontSize="6xl"
                fontWeight="bold"
                color="whiteAlpha.100"
                pointerEvents="none"
                zIndex={1}
                whiteSpace="nowrap"
                userSelect="none"
                className="watermark"
              >
                ElevationEMS
              </Box>

              <Box position="relative" zIndex={2}>
                {shareToken?.scenario?.scenarioOutput && (
                  <React.Suspense fallback={<Spinner size="lg" />}>
                    <ScenarioDisplay 
                      scenario={shareToken.scenario.scenarioOutput} 
                      metadata={{
                        id: shareToken.scenario.id,
                        levelOfCare: shareToken.scenario.levelOfCare,
                        mechanismNatureOfInjury: shareToken.scenario.majorSystemInvolved,
                        title: shareToken.scenario.title
                      }}
                      evaluationMode={true}
                      evaluationData={liveScoreChecks}
                      onEvaluationChange={handleLiveScoreCheck}
                      showToolbar={false}
                      hideRawInput={true}
                    />
                  </React.Suspense>
                )}
              </Box>
            </Box>

            {/* Evaluation Form Section */}
            <Box bg="gray.800" p={6} borderRadius="lg">
              <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <HStack justify="space-between" mb={4} align="start" wrap="wrap">
                      <VStack align="start" spacing={2}>
                        <Heading size="lg">
                          {getEvaluationFormTitle(shareToken?.simulationSession?.evaluationForm)}
                        </Heading>
                        {renderEvaluationFormSwitcher()}
                      </VStack>
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                        {liveScoreChecks ? Object.values(liveScoreChecks).reduce((total, category) =>
                          total + Object.values(category).filter(Boolean).length, 0
                        ) : 0} items live scored
                      </Badge>
                    </HStack>
                    
                    {/* Overall progress indicator */}
                    <Box mb={4}>
                      <Text fontSize="sm" color="gray.400" mb={2}>Overall Completion</Text>
                      <Progress 
                        value={(() => {
                          const allSections = Object.keys(nremtEval).filter(k => k !== 'overallPerformance');
                          const totalFields = allSections.reduce((sum, section) => 
                            sum + Object.keys(nremtEval[section]).filter(k => k !== 'notes').length, 0
                          );
                          const completedFields = allSections.reduce((sum, section) => 
                            sum + Object.entries(nremtEval[section])
                              .filter(([k, v]) => k !== 'notes' && v !== 'notPerformed').length, 0
                          );
                          return totalFields > 0 ? (completedFields / totalFields) * 100 : 0;
                        })()}
                        colorScheme="green"
                        size="sm"
                        borderRadius="md"
                      />
                    </Box>
                  </Box>

                  {renderStudentAssignmentSection()}

                  {/* Evaluation Section Component */}
                  {shareToken?.simulationSession?.evaluationForm === "nremt_trauma" && renderTraumaRubricSection()}
                  {shareToken?.simulationSession?.evaluationForm === "nremt_integrated_ooh" && renderIntegratedRubricSection()}
                  {shareToken?.simulationSession?.evaluationForm !== "nremt_trauma" &&
                    shareToken?.simulationSession?.evaluationForm !== "nremt_integrated_ooh" &&
                    (() => {
                    const EvalSection = ({ title, sectionKey, fields, liveScores, evalState, onUpdate }) => {
                      const fieldKeys = Object.keys(fields).filter(k => k !== 'notes');
                      const completed = fieldKeys.filter(k => evalState[k] !== 'notPerformed').length;
                      const total = fieldKeys.length;
                      
                      return (
                        <AccordionItem>
                          <AccordionButton bg={sectionNeedsAttention(sectionKey, evalState, liveScores) ? 'red.900' : undefined}>
                            <Box flex="1" textAlign="left">
                              <HStack>
                                <Text fontSize="lg" fontWeight="bold" color={sectionNeedsAttention(sectionKey, evalState, liveScores) ? 'red.300' : undefined}>{title}</Text>
                                <Badge colorScheme={completed === total ? 'green' : completed > 0 ? 'yellow' : 'gray'}>
                                  {completed}/{total}
                                </Badge>
                                {sectionNeedsAttention(sectionKey, evalState, liveScores) && (
                                  <Badge colorScheme="red" size="sm">Needs Performance Rating</Badge>
                                )}
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <VStack spacing={4} align="stretch">
                              {fieldKeys.map((key) => {
                                const liveValue = liveScores?.[sectionKey]?.[key];
                                const currentValue = evalState[key];
                                const wasLiveScored = liveValue === true;
                                const wasModified = wasLiveScored && currentValue !== 'performed';
                                
                                return (
                                  <FormControl key={key}>
                                    <HStack spacing={2} mb={1}>
                                      <FormLabel mb={0} fontSize="md">{fields[key]}</FormLabel>
                                      {wasLiveScored && !wasModified && (
                                        <Badge colorScheme="green" size="sm">Live</Badge>
                                      )}
                                      {wasModified && (
                                        <Badge colorScheme="yellow" size="sm">Modified</Badge>
                                      )}
                                      {!wasLiveScored && currentValue !== 'notPerformed' && (
                                        <Badge colorScheme="gray" size="sm">Manual</Badge>
                                      )}
                                    </HStack>
                                    <RadioGroup
                                      value={currentValue}
                                      onChange={(val) => onUpdate(sectionKey, key, val)}
                                    >
                                      <Stack direction="row" spacing={6} flexWrap="wrap">
                                        <Radio value="performed" size="lg">Performed</Radio>
                                        <Radio value="notPerformed" size="lg">Not Performed</Radio>
                                        <Radio value="incorrect" size="lg">Incorrect</Radio>
                                        <Radio value="na" size="lg">N/A</Radio>
                                      </Stack>
                                    </RadioGroup>
                                  </FormControl>
                                );
                              })}
                              <FormControl isRequired={completed > 0}>
                                <HStack spacing={2} mb={1}>
                                  <FormLabel mb={0} color={!evalState.performance && completed > 0 ? 'red.400' : undefined}>
                                    Section Performance {completed > 0 && !evalState.performance && '(Required)'}
                                  </FormLabel>
                                  {!evalState.performance && completed > 0 && (
                                    <Badge colorScheme="red" size="sm">Required</Badge>
                                  )}
                                </HStack>
                                <RadioGroup
                                  value={evalState.performance || ''}
                                  onChange={(val) => onUpdate(sectionKey, 'performance', val || null)}
                                >
                                  <Stack direction="row" spacing={6} flexWrap="wrap">
                                    <Radio value="Exemplary" size="lg">Exemplary</Radio>
                                    <Radio value="Exceeds Expectations" size="lg">Exceeds</Radio>
                                    <Radio value="Proficient" size="lg">Proficient</Radio>
                                    <Radio value="Near Pass" size="lg">Near Pass</Radio>
                                    <Radio value="Fail" size="lg">Fail</Radio>
                                    <Radio value="Critical Deficiency" size="lg">Critical</Radio>
                                  </Stack>
                                </RadioGroup>
                              </FormControl>
                              <FormControl>
                                <FormLabel>Section Notes</FormLabel>
                                <Textarea
                                  value={evalState.notes}
                                  onChange={(e) => onUpdate(sectionKey, 'notes', e.target.value)}
                                  placeholder="Additional notes for this section..."
                                  bg="gray.700"
                                  rows={2}
                                />
                              </FormControl>
                            </VStack>
                          </AccordionPanel>
                        </AccordionItem>
                      );
                    };

                    const handleEvalUpdate = (section, field, value) => {
                      setNremtEval(prev => ({
                        ...prev,
                        [section]: {
                          ...prev[section],
                          [field]: value
                        }
                      }));
                    };

                    return (
                      <Accordion allowMultiple defaultIndex={[0]}>
                        <EvalSection
                          title="Scene Size-Up"
                          sectionKey="sceneSizeUp"
                          fields={fieldLabels.sceneSizeUp}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.sceneSizeUp}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="Primary Assessment"
                          sectionKey="primaryAssessment"
                          fields={fieldLabels.primaryAssessment}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.primaryAssessment}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="SAMPLE History"
                          sectionKey="sample"
                          fields={fieldLabels.sample}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.sample}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="OPQRST Assessment"
                          sectionKey="opqrst"
                          fields={fieldLabels.opqrst}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.opqrst}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="Vital Signs"
                          sectionKey="vitalSigns"
                          fields={fieldLabels.vitalSigns}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.vitalSigns}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="Physical Examination"
                          sectionKey="physicalExam"
                          fields={fieldLabels.physicalExam}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.physicalExam}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="Interventions"
                          sectionKey="interventions"
                          fields={fieldLabels.interventions}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.interventions}
                          onUpdate={handleEvalUpdate}
                        />
                        <EvalSection
                          title="Reassessment"
                          sectionKey="reassessment"
                          fields={fieldLabels.reassessment}
                          liveScores={liveScoreChecks}
                          evalState={nremtEval.reassessment}
                          onUpdate={handleEvalUpdate}
                        />
                      </Accordion>
                    );
                  })()}

                  {/* Overall Performance Section */}
                  <Box>
                    <Heading size="md" mb={4}>Overall Performance</Heading>
                    <FormControl>
                      <FormLabel>Overall Performance Rating</FormLabel>
                      <RadioGroup
                        value={nremtEval.overallPerformance.performance || ''}
                        onChange={(val) => {
                          setNremtEval(prev => ({
                            ...prev,
                            overallPerformance: {
                              ...prev.overallPerformance,
                              performance: val || null
                            }
                          }));
                        }}
                      >
                        <Stack direction="row" spacing={6} flexWrap="wrap">
                          <Radio value="Exemplary" size="lg">Exemplary</Radio>
                          <Radio value="Exceeds Expectations" size="lg">Exceeds Expectations</Radio>
                          <Radio value="Proficient" size="lg">Proficient</Radio>
                          <Radio value="Near Pass" size="lg">Near Pass</Radio>
                          <Radio value="Fail" size="lg">Fail</Radio>
                          <Radio value="Critical Deficiency" size="lg">Critical Deficiency</Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                    <FormControl mt={4}>
                      <FormLabel>Overall Comments</FormLabel>
                      <Textarea
                        value={nremtEval.overallPerformance.instructorNotes}
                        onChange={(e) => {
                          setNremtEval(prev => ({
                            ...prev,
                            overallPerformance: {
                              ...prev.overallPerformance,
                              instructorNotes: e.target.value
                            }
                          }));
                        }}
                        placeholder="Overall evaluation comments..."
                        bg="gray.700"
                        rows={6}
                      />
                    </FormControl>
                  </Box>

                  <Divider />

                      {renderScenarioTrackingSection()}
                  {renderCriticalCriteriaSection()}

                  {/* Validation warning for incomplete sections */}
                  {shareToken?.simulationSession?.evaluationForm === "nremt_medical" && (() => {
                    const sectionsNeedingAttention = Object.keys(fieldLabels)
                      .filter(sectionKey => sectionNeedsAttention(sectionKey, nremtEval[sectionKey], liveScoreChecks));
                    
                    if (sectionsNeedingAttention.length > 0) {
                      return (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <Box flex="1">
                            <AlertTitle>Incomplete Sections</AlertTitle>
                            <AlertDescription>
                              The following sections have evaluated items but are missing performance ratings:
                              <VStack align="start" mt={2} spacing={1}>
                                {sectionsNeedingAttention.map(key => {
                                  const sectionTitle = 
                                    key === 'sceneSizeUp' ? 'Scene Size-Up' :
                                    key === 'primaryAssessment' ? 'Primary Assessment' :
                                    key === 'sample' ? 'SAMPLE History' :
                                    key === 'opqrst' ? 'OPQRST Assessment' :
                                    key === 'vitalSigns' ? 'Vital Signs' :
                                    key === 'physicalExam' ? 'Physical Examination' :
                                    key === 'interventions' ? 'Interventions' :
                                    key === 'reassessment' ? 'Reassessment' :
                                    key;
                                  return (
                                    <Text key={key} fontWeight="bold" fontSize="sm">
                                      • {sectionTitle}
                                    </Text>
                                  );
                                })}
                              </VStack>
                            </AlertDescription>
                          </Box>
                        </Alert>
                      );
                    }
                    return null;
                  })()}

                  {submitError && (
                    <Alert status="error">
                      <AlertIcon />
                      <AlertDescription>{submitError.message}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    isLoading={submitting}
                    loadingText="Submitting..."
                  >
                    Submit Evaluation
                  </Button>
                </VStack>
              </form>
            </Box>
          </VStack>
        )}
      </Container>
    </>
  );
}
